/* Brandlabs section components: header, menus, search, cart, product, collection. */
import { fine, reducedMotion, isDesktop, isTablet, ThemeElement, belongsTo, fetchSectionElement, whenIdle } from 'theme';

const Theme = window.Theme;
const define = (name, cls) => customElements.get(name) || customElements.define(name, cls);
const settings = window.themeSettings || {};

/* Horizontal swipe: calls back with 1 (next) or -1 (previous) for a flick of more than 80px sideways, under 80px
   vertically, within 500ms. `holdPage` stops the page from scrolling while the finger moves sideways. */
const onSwipe = (element, callback, { holdPage = false } = {}) => {
  let start = null;
  element.addEventListener('pointerdown', (e) => (start = { x: e.clientX, y: e.clientY, t: Date.now() }));
  element.addEventListener('pointerup', (e) => {
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = Math.abs(e.clientY - start.y);
    if (Math.abs(dx) > 80 && dy < 80 && Date.now() - start.t < 500) callback(dx < 0 ? 1 : -1);
    start = null;
  });
  if (holdPage) {
    element.addEventListener('touchmove', (e) => {
      if (start && Math.abs(e.touches[0].clientX - start.x) > 10) e.preventDefault();
    }, { passive: false });
  }
};

/* Staggered "fade in, rise" for a list of elements */
const stagger = (elements, { rise = 8, duration = 150, step = 100, delay = 0, easing = 'ease' } = {}) => {
  if (reducedMotion()) return Promise.resolve();
  const animations = [...elements].map((el, i) =>
    el.animate([{ opacity: 0, transform: `translateY(${rise}px)` }, { opacity: 1, transform: 'none' }], {
      duration,
      delay: delay + (settings.staggerMenu ? i * step : 0),
      easing,
      fill: 'backwards',
    })
  );
  return Promise.all(animations.map((a) => a.finished.catch(() => {})));
};
Theme.stagger = stagger;

/* ===========================================================================
   Header: sticky, hide on scroll, transparent state, desktop menus
=========================================================================== */
class XHeader extends ThemeElement {
  setup() {
    this.wrapper = this.closest('.shopify-section');
    this.wrapper?.classList.add('header-section');
    if (this.hasAttribute('data-sticky')) this.wrapper?.setAttribute('data-sticky', '');
    this.openPanels = new Set();
    this.lastY = window.scrollY;
    this.turnY = window.scrollY;
    this.setupMenus();
    this.onScroll = this.onScroll.bind(this);
    this.addEventListener('pointerenter', () => { this.setAttribute('data-hovered', ''); this.updateSolid(); });
    this.addEventListener('pointerleave', () => { this.removeAttribute('data-hovered'); this.updateSolid(); });
    if (this.hasAttribute('data-hide-on-scroll') && fine()) {
      document.addEventListener('pointermove', (event) => {
        if (event.clientY < 100 && this.wrapper?.classList.contains('is-hidden')) this.show();
      });
    }
    document.addEventListener('dialog:show', (e) => this.onDialog(e, true));
    document.addEventListener('dialog:hide', (e) => this.onDialog(e, false));
    this.updateSolid();
    Theme.updateStickyOffset?.();
    this.updateAnnouncementStickiness();
    this.fitMenu();
    new ResizeObserver(() => this.fitMenu()).observe(this);
    document.fonts?.ready.then(() => this.fitMenu());
  }

  /* Inline menus: when the links do not fit on one line (long labels, a narrow desktop, the editor preview) the links
     first move closer together, and only if that is still not enough does the header fall back to the menu button and
     drawer, as on mobile, instead of wrapping the menu onto a second row. The menu is measured in its inline state, so
     the outcome does not depend on the previous one. */
  fitMenu() {
    const list = this.querySelector('.header__menu-list');
    if (!list || !this.querySelector('.header__nav-toggle')) return;
    this.classList.remove('is-menu-collapsed', 'is-menu-tight');
    if (isDesktop()) {
      const items = list.children;
      const overflows = () => (items.length > 1 && items[items.length - 1].offsetTop > items[0].offsetTop) || list.scrollWidth > list.clientWidth + 1;
      if (overflows()) {
        this.classList.add('is-menu-tight');
        if (overflows()) this.classList.replace('is-menu-tight', 'is-menu-collapsed');
      }
    }
    this.classList.add('is-menu-fitted');
  }

  connected() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnected() {
    window.removeEventListener('scroll', this.onScroll);
  }

  updateAnnouncementStickiness() {
    const bar = document.querySelector('.announcement-section[data-sticky]');
    document.documentElement.style.setProperty('--announcement-sticky-height', bar ? `${bar.offsetHeight}px` : '0px');
  }

  onDialog(event, open) {
    const dialog = event.target;
    if (!(dialog instanceof HTMLElement)) return;
    const inHeader = this.wrapper?.contains(dialog) || dialog.id?.startsWith('header-search') || dialog.id?.startsWith('header-');
    if (!inHeader) return;
    open ? this.openPanels.add(dialog) : this.openPanels.delete(dialog);
    if (dialog.classList.contains('header-search')) {
      const rect = this.wrapper.getBoundingClientRect();
      document.documentElement.style.setProperty('--header-top', `${Math.max(rect.top, 0)}px`);
    }
    this.updateSolid();
  }

  get scrolledPastThreshold() {
    const firstGroupSection = document.querySelector('.shopify-section-group-header-group');
    if (firstGroupSection?.classList.contains('announcement-section') && !firstGroupSection.hasAttribute('data-sticky')) {
      return window.scrollY > firstGroupSection.offsetHeight;
    }
    return window.scrollY > 10;
  }

  updateSolid() {
    const solid = this.hasAttribute('data-hovered') || this.openPanels.size > 0 || this.scrolledPastThreshold;
    this.classList.toggle('is-solid', solid);
  }

  onScroll() {
    const y = window.scrollY;
    this.updateSolid();
    if (this.hasAttribute('data-hide-on-scroll')) {
      const goingDown = y > this.lastY;
      if (goingDown !== this.goingDown) {
        this.turnY = this.lastY;
        this.goingDown = goingDown;
      }
      if (goingDown && y - this.turnY > 100 && this.openPanels.size === 0) this.hide();
      else if (!goingDown) this.show();
    }
    this.lastY = y;
  }

  hide() {
    if (this.wrapper.classList.contains('is-hidden')) return;
    this.wrapper.classList.add('is-hidden');
    Theme.updateStickyOffset?.();
  }

  show() {
    if (!this.wrapper.classList.contains('is-hidden')) return;
    this.wrapper.classList.remove('is-hidden');
    Theme.updateStickyOffset?.();
  }

  /* ---- Desktop dropdowns and mega menus ---- */
  setupMenus() {
    const hoverMode = this.getAttribute('data-menu-trigger') === 'hover';
    this.querySelectorAll('[data-panel]').forEach((trigger) => {
      const panel = this.querySelector(`#${trigger.getAttribute('data-panel')}`);
      if (!panel) return;
      const item = trigger.parentElement;
      const isTop = trigger.classList.contains('header__menu-link');

      trigger.addEventListener('click', (event) => {
        if (trigger.getAttribute('aria-expanded') === 'true') return; // second click follows the link
        event.preventDefault();
        this.openPanel(trigger, panel, isTop);
      });
      trigger.addEventListener('keydown', (event) => {
        if (event.key === ' ') {
          event.preventDefault();
          trigger.getAttribute('aria-expanded') === 'true' ? this.closePanel(trigger, panel) : this.openPanel(trigger, panel, isTop);
        }
      });

      if (hoverMode) {
        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(panel._closeTimer);
          this.openPanel(trigger, panel, isTop);
        });
        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          panel._closeTimer = setTimeout(() => this.closePanel(trigger, panel), 250);
        });
      }

      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) this.closePanel(trigger, panel);
      });
      panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.closePanel(trigger, panel);
          trigger.focus();
        }
      });
    });
    document.addEventListener('click', (event) => {
      if (this.contains(event.target)) return;
      this.querySelectorAll('[data-panel][aria-expanded="true"]').forEach((t) => this.closePanel(t, this.querySelector(`#${t.getAttribute('data-panel')}`)));
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = this.querySelector('.header__menu-link[aria-expanded="true"]');
      if (open) {
        this.closePanel(open, this.querySelector(`#${open.getAttribute('data-panel')}`));
        open.focus();
      }
    });
  }

  openPanel(trigger, panel, isTop) {
    if (isTop) {
      // Pointing at another top-level item closes the previous one at once
      this.querySelectorAll('.header__menu-link[aria-expanded="true"]').forEach((other) => {
        if (other !== trigger) this.closePanel(other, this.querySelector(`#${other.getAttribute('data-panel')}`), true);
      });
    } else {
      trigger.closest('.dropdown__list')?.querySelectorAll(':scope > li > [aria-expanded="true"]').forEach((other) => {
        if (other !== trigger) this.closePanel(other, this.querySelector(`#${other.getAttribute('data-panel')}`), true);
      });
    }
    clearTimeout(panel._closeTimer);
    clearTimeout(panel._hideTimer);
    if (trigger.getAttribute('aria-expanded') === 'true') return;
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    void panel.offsetWidth;
    panel.classList.add('is-open');
    this.openPanels.add(panel);
    this.updateSolid();

    if (reducedMotion()) return;
    if (panel.classList.contains('mega-menu')) {
      const columns = panel.querySelectorAll('[data-stagger]');
      if (settings.staggerMenu) {
        stagger(columns, { rise: 8, duration: 300, step: 100 });
        const images = panel.querySelector('[data-stagger-images]');
        images?.animate({ opacity: [0, 1] }, { duration: 300, delay: Math.max(0, columns.length * 100 + 300 - 150 - 100), fill: 'backwards' });
      }
    } else if (settings.staggerMenu) {
      stagger(panel.querySelectorAll(':scope > ul > [data-stagger]'), { delay: 100 });
    }
  }

  closePanel(trigger, panel, instant = false) {
    if (!panel || trigger.getAttribute('aria-expanded') !== 'true') return;
    trigger.setAttribute('aria-expanded', 'false');
    panel.querySelectorAll('[aria-expanded="true"][data-panel]').forEach((t) => this.closePanel(t, this.querySelector(`#${t.getAttribute('data-panel')}`), true));
    panel.classList.remove('is-open');
    this.openPanels.delete(panel);
    this.updateSolid();
    clearTimeout(panel._hideTimer);
    if (instant) {
      panel.hidden = true;
    } else {
      panel._hideTimer = setTimeout(() => (panel.hidden = true), 400);
    }
  }
}
define('x-header', XHeader);

/* ===========================================================================
   Sidebar menu
=========================================================================== */
class XSidebarMenu extends ThemeElement {
  setup() {
    this.dialog = this.closest('x-dialog');
    this.main = this.querySelector('[data-panel="main"]');
    this.querySelectorAll('[data-subpanel]').forEach((row) => row.addEventListener('click', () => this.toggleSubpanel(row)));
    this.querySelectorAll('[data-back]').forEach((btn) => btn.addEventListener('click', () => this.back()));
    // Menu tabs: choosing another tab goes back to the main list first.
    this.querySelectorAll('.sidebar-menu__tab-list [role="tab"]').forEach((tab) => tab.addEventListener('click', () => this.back()));
    // Dialog events bubble, so popovers inside the menu (country, language) are ignored here.
    const own = (handler) => (event) => event.target === this.dialog && handler();
    this.dialog?.addEventListener('dialog:after-show', own(() => this.revealMain()));
    this.dialog?.addEventListener('dialog:before-show', own(() => this.prepare()));
    this.dialog?.addEventListener('dialog:after-hide', own(() => this.reset()));
    this.dialog?.addEventListener('dialog:hide', own(() => this.closeSubpanels(true)));
  }

  prepare() {
    if (reducedMotion()) return;
    this.querySelectorAll('[data-panel="main"] [data-stagger], .sidebar-menu__footer').forEach((el) => (el.style.opacity = '0'));
  }

  // The rows on show in the main panel: with tabs, the tab list and the selected tab's rows.
  mainRows() {
    return [...this.main.querySelectorAll('[data-stagger]')].filter((el) => !el.closest('[hidden]'));
  }

  // Main rows rise one after another, then the footer (on opening and when coming back from a subpanel).
  async revealMain() {
    const rows = this.mainRows();
    const footer = this.querySelector('.sidebar-menu__footer');
    this.main.querySelectorAll('[data-stagger]').forEach((el) => (el.style.opacity = ''));
    if (footer) footer.style.opacity = '';
    if (reducedMotion()) return;
    if (footer) footer.style.opacity = '0';
    await stagger(rows);
    if (footer) {
      footer.style.opacity = '';
      footer.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: 300, easing: 'ease' });
    }
  }

  get openSubpanel() {
    return this.querySelector('.sidebar-menu__subpanel.is-open');
  }

  async toggleSubpanel(row) {
    const panel = this.querySelector(`#${row.getAttribute('data-subpanel')}`);
    if (!panel) return;
    if (row.getAttribute('aria-expanded') === 'true' && isDesktop()) return this.closeSubpanels();
    const current = this.openSubpanel;
    this.querySelectorAll('[data-subpanel]').forEach((r) => r.setAttribute('aria-expanded', String(r === row)));

    if (isDesktop()) {
      if (current && current !== panel) {
        await current.animate({ opacity: [1, 0] }, { duration: 150, fill: 'forwards' }).finished;
        current.getAnimations().forEach((a) => a.cancel());
        current.classList.remove('is-open');
        current.hidden = true;
        panel.hidden = false;
        panel.classList.add('is-open');
        stagger(panel.querySelectorAll(':scope > ul > [data-stagger]'));
      } else {
        panel.hidden = false;
        void panel.offsetWidth;
        panel.classList.add('is-open');
        stagger(panel.querySelectorAll(':scope > ul > [data-stagger]'), { delay: 150 });
      }
      panel.querySelector('[data-promo]')?.animate({ opacity: [0, 1] }, { duration: 450, fill: 'backwards' });
      return;
    }

    // Mobile: the main list fades out while the subpanel fades in over it; its rows rise once it is in.
    panel.hidden = false;
    panel.classList.add('is-open');
    if (reducedMotion()) {
      this.main.style.visibility = 'hidden';
      return;
    }
    const rows = [panel.querySelector('[data-back]'), ...panel.querySelectorAll(':scope > ul > [data-stagger]')].filter(Boolean);
    rows.forEach((el) => (el.style.opacity = '0'));
    this.main.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateX(-10px)' }], { duration: 250, easing: 'ease', fill: 'forwards' })
      .finished.then(() => this.openSubpanel === panel && (this.main.style.visibility = 'hidden'));
    await panel.animate({ opacity: [0, 1] }, { duration: 300, easing: 'ease' }).finished;
    rows.forEach((el) => (el.style.opacity = ''));
    stagger(rows);
    panel.querySelector('[data-promo]')?.animate({ opacity: [0, 1] }, { duration: 450, fill: 'backwards' });
  }

  async back() {
    const panel = this.openSubpanel;
    if (!panel) return;
    this.querySelectorAll('[data-subpanel]').forEach((r) => r.setAttribute('aria-expanded', 'false'));
    const close = () => {
      panel.getAnimations().forEach((a) => a.cancel());
      panel.classList.remove('is-open');
      panel.hidden = true;
    };
    if (isDesktop()) {
      if (!reducedMotion()) await panel.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateX(10px)' }], { duration: 300, easing: 'ease', fill: 'forwards' }).finished;
      close();
      if (!reducedMotion()) await Theme.wait(500);
      this.main.getAnimations().forEach((a) => a.cancel());
      this.main.style.visibility = '';
      return stagger(this.mainRows());
    }
    // Phones: the way in, reversed. The subpanel fades out while the main rows come back under it, so the tap
    // answers at once instead of after the whole fade.
    this.main.getAnimations().forEach((a) => a.cancel());
    this.main.style.visibility = '';
    if (reducedMotion()) return close();
    this.prepare();
    const fade = panel.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateX(10px)' }], { duration: 200, easing: 'ease', fill: 'forwards' }).finished;
    this.revealMain();
    await fade;
    close();
  }

  closeSubpanels(instant = false) {
    this.querySelectorAll('.sidebar-menu__subpanel.is-open').forEach((panel) => {
      panel.classList.remove('is-open');
      if (instant || !isDesktop()) panel.hidden = true;
      else setTimeout(() => !panel.classList.contains('is-open') && (panel.hidden = true), 300);
    });
    this.querySelectorAll('[data-subpanel]').forEach((r) => r.setAttribute('aria-expanded', 'false'));
  }

  reset() {
    this.closeSubpanels(true);
    this.main.getAnimations().forEach((a) => a.cancel());
    this.main.style.visibility = '';
    if (!this.hasAttribute('open-accordions')) this.querySelectorAll('details[open]').forEach((d) => (d.open = false));
    const input = this.querySelector('[data-predictive-input]');
    if (input && input.value) {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}
define('x-sidebar-menu', XSidebarMenu);

/* ===========================================================================
   Predictive search: binds to the nearest [data-predictive-input]
=========================================================================== */
const searchCache = new Map();

class PredictiveSearch extends ThemeElement {
  setup() {
    const scope = this.closest('.overlay-panel') || document;
    this.input = scope.querySelector('[data-predictive-input]');
    this.form = this.input?.form;
    if (!this.input) return;
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(this.input.value.trim()), 280);
    });
    this.form?.addEventListener('reset', () => {
      setTimeout(() => {
        this.clear();
        this.input.focus();
      });
    });
    this.form?.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) event.preventDefault();
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-submit]')) this.form?.requestSubmit();
    });
  }

  clear() {
    this.controller?.abort();
    this.innerHTML = '';
    this.hidden = true;
  }

  async search(query) {
    this.controller?.abort();
    if (!query) return this.clear();
    if (searchCache.has(query)) return this.render(searchCache.get(query));
    this.controller = new AbortController();
    const url = new URL(settings.routes?.predictiveSearch || '/search/suggest', location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'product,collection,article,page,query');
    Theme.loading.start();
    try {
      let response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) {
        // Fallback to the regular search when predictive search is unavailable
        const fallback = new URL(settings.routes?.search || '/search', location.origin);
        fallback.searchParams.set('q', query);
        fallback.searchParams.set('section_id', 'predictive-search');
        fallback.searchParams.set('type', 'product,article,page');
        response = await fetch(fallback, { signal: this.controller.signal });
      }
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.predictive-search')?.outerHTML || '';
      searchCache.set(query, content);
      if (this.input.value.trim() === query) this.render(content);
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      Theme.loading.end();
    }
  }

  render(html) {
    this.innerHTML = html;
    this.hidden = !html;
  }
}
define('predictive-search', PredictiveSearch);

/* ===========================================================================
   Announcement bar
=========================================================================== */
class AnnouncementBar extends ThemeElement {
  setup() {
    this.wrapper = this.closest('.shopify-section');
    this.wrapper?.classList.add('announcement-section');
    if (this.hasAttribute('data-sticky')) this.wrapper?.setAttribute('data-sticky', '');
    this.items = [...this.querySelectorAll('[data-message]')];
    this.index = 0;
    this.setupSwipe();
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
      if (event.key === 'ArrowRight') this.go(this.index + 1);
    });
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.play()));
    this.mobile = window.matchMedia('(max-width: 699px)');
    this.mobile.addEventListener('change', () => this.updateRotation());
    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        this.pause(true);
        this.go(this.items.indexOf(event.target.closest('[data-message]')), true);
      });
      this.addEventListener('shopify:block:deselect', () => {
        this.locked = false;
        this.play();
      });
    }
    this.updateRotation();
    this.play();
    requestAnimationFrame(() => {
      document.querySelector('x-header')?.updateAnnouncementStickiness?.();
      Theme.updateStickyOffset?.();
    });
  }

  get activeItems() {
    return this.items.filter((item) => !item.hasAttribute('data-excluded'));
  }

  /* Phone: the free shipping block may take over the rotation once reached */
  updateRotation() {
    const takeover = this.items.find((item) => item.hasAttribute('data-only-when-reached') && item.classList.contains('is-reached'));
    this.items.forEach((item) => item.toggleAttribute('data-excluded', Boolean(takeover && this.mobile.matches && item !== takeover)));
    const current = this.items[this.index];
    if (current?.hasAttribute('data-excluded') && takeover) this.go(this.items.indexOf(takeover), true);
  }

  play() {
    if (this.locked || !this.hasAttribute('autoplay') || this.activeItems.length < 2) return;
    clearInterval(this.timer);
    this.timer = setInterval(() => this.go(this.index + 1), Number(this.getAttribute('autoplay')) * 1000);
  }

  pause(lock = false) {
    clearInterval(this.timer);
    if (lock) this.locked = true;
  }

  async go(target, instant = false) {
    const active = this.activeItems;
    if (active.length < 1) return;
    const currentEl = this.items[this.index];
    let pos = active.indexOf(currentEl);
    if (pos === -1) pos = 0;
    let nextEl;
    if (typeof target === 'number' && this.items[target] && instant) nextEl = this.items[target];
    else {
      const delta = target - this.index;
      nextEl = active[(pos + (delta >= 0 ? 1 : -1) + active.length) % active.length];
    }
    if (!nextEl || nextEl === currentEl) return;
    this.index = this.items.indexOf(nextEl);
    // A tap during a transition cuts it short: the shown message always matches this.index.
    const token = (this.token = (this.token || 0) + 1);
    this.items.forEach((item) => item.getAnimations().forEach((a) => a.cancel()));
    if (instant || reducedMotion()) {
      this.items.forEach((item) => item.classList.toggle('is-current', item === nextEl));
    } else {
      this.items.forEach((item) => item.classList.toggle('is-current', item === currentEl));
      const leave = currentEl.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-10px)' }], { duration: 250, easing: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)', fill: 'forwards' });
      await leave.finished.catch(() => {});
      if (token !== this.token) return;
      leave.cancel();
      currentEl.classList.remove('is-current');
      nextEl.classList.add('is-current');
      nextEl.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: 400, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)' });
    }
    this.play();
  }

  setupSwipe() {
    onSwipe(this, (direction) => this.go(this.index + direction));
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  /* Free-shipping messages follow the cart: Cart renders this section in the same request as the cart change. */
  renderFreeShipping(sections) {
    const items = this.querySelectorAll('[data-free-shipping]');
    const html = sections?.[this.sectionId];
    if (!items.length || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    items.forEach((item) => {
      const fresh = doc.getElementById(item.id);
      if (!fresh) return;
      item.querySelector('[data-free-shipping-content]').innerHTML = fresh.querySelector('[data-free-shipping-content]').innerHTML;
      item.classList.toggle('is-reached', fresh.classList.contains('is-reached'));
    });
    this.updateRotation();
  }
}
define('announcement-bar', AnnouncementBar);

/* Back/forward cache: refresh cart state */
window.addEventListener('pageshow', (event) => {
  if (event.persisted) document.dispatchEvent(new CustomEvent('cart:refresh'));
});

/* ===========================================================================
   Pop-ins: newsletter popup and privacy banner
=========================================================================== */
class PopIn extends ThemeElement {
  setup() {
    this.querySelectorAll('[data-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
  }

  open() {
    this.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
  }

  close() {
    this.classList.remove('is-open');
    setTimeout(() => (this.hidden = true), 400);
  }

  show() {
    this.open();
  }

  hide() {
    this.close();
  }
}

const storage = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {}
  },
};

class NewsletterPopup extends PopIn {
  setup() {
    super.setup();
    if (window.Shopify?.designMode) return;
    if (this.querySelector('[data-subscribed]')) {
      storage.set('brandlabs:newsletter-subscribed', 'true');
      return this.open();
    }
    if (this.querySelector('[data-form-error]')) return this.open();
    if (storage.get('brandlabs:newsletter-subscribed')) return;
    if (this.hasAttribute('data-once') && storage.get('brandlabs:newsletter-seen')) return;
    setTimeout(() => {
      this.open();
      storage.set('brandlabs:newsletter-seen', 'true');
    }, Number(this.dataset.delay || 0) * 1000);
  }
}
define('newsletter-popup', NewsletterPopup);

class PrivacyBanner extends PopIn {
  setup() {
    super.setup();
    if (window.Shopify?.designMode) return;
    this.querySelector('[data-accept]')?.addEventListener('click', () => this.consent(true));
    this.querySelector('[data-decline]')?.addEventListener('click', () => this.consent(false));
    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) return;
      if (window.Shopify.customerPrivacy?.shouldShowBanner?.()) this.open();
    });
  }

  consent(accepted) {
    window.Shopify?.customerPrivacy?.setTrackingConsent?.({ analytics: accepted, marketing: accepted, preferences: accepted }, () => {});
    this.close();
  }
}
define('privacy-banner', PrivacyBanner);

/* ===========================================================================
   Cart: shared API. Every change dispatches `cart:change` and re-renders the drawer.
=========================================================================== */
const Cart = (Theme.Cart = {
  get drawer() {
    return document.getElementById('cart-drawer');
  },

  get onCartPage() {
    return Boolean(document.querySelector('[data-main-cart]'));
  },

  // Sections re-rendered with every cart change, in the same request (Section Rendering API).
  sectionsToRender() {
    const ids = [];
    const drawer = this.drawer;
    if (drawer?.dataset.sectionId) ids.push(drawer.dataset.sectionId);
    document.querySelectorAll('announcement-bar').forEach((bar) => {
      if (bar.querySelector('[data-free-shipping]') && bar.sectionId) ids.push(bar.sectionId);
    });
    return ids;
  },

  async request(url, body) {
    Theme.loading.start();
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionsToRender(), sections_url: location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw data;
      return data;
    } finally {
      Theme.loading.end();
    }
  },

  /* Theme settings › Cart › Free item in every order. The free item rides along with the first add, in the same request,
     and goes when only gift lines are left (spec 11 §12.5b). Its line carries _gift = auto. */
  hasAutoGift() {
    return this.cart ? this.cart.items.some((item) => item.properties?._gift === 'auto') : settings.autoGiftInCart;
  },

  autoGiftItem() {
    return { id: settings.autoGift, quantity: 1, properties: { _gift: 'auto' } };
  },

  // The add form as a cart item, so the free item can join it in one JSON request. The free item goes first in that
  // request: the cart lists the newest line on top, so the product the shopper added leads.
  itemFromForm(formData) {
    const item = { properties: {} };
    for (const [name, value] of formData) {
      const property = name.match(/^properties\[(.+)\]$/);
      if (property) item.properties[property[1]] = value;
      else if (['id', 'quantity', 'selling_plan'].includes(name)) item[name] = value;
    }
    return item;
  },

  async add(formData) {
    Theme.loading.start();
    let ended = false;
    try {
      const withGift = settings.autoGift && String(formData.get('id')) !== String(settings.autoGift) && !this.hasAutoGift();
      // A file upload cannot travel as JSON: that form goes as it is and the free item follows it.
      const merge = withGift && ![...formData.values()].some((value) => value instanceof File);
      const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      const request = merge
        ? { body: JSON.stringify({ items: [this.autoGiftItem(), this.itemFromForm(formData)] }), headers: { ...headers, 'Content-Type': 'application/json' } }
        : { body: formData, headers };
      let response = await fetch(`${settings.routes.cartAdd}.js`, { method: 'POST', ...request });
      // The free item must never block a sale (sold out, removed): the item is added on its own instead.
      if (merge && !response.ok) response = await fetch(`${settings.routes.cartAdd}.js`, { method: 'POST', body: formData, headers });
      const data = await response.json();
      if (!response.ok || data.status) throw data;
      if (withGift && !merge) {
        await fetch(`${settings.routes.cartAdd}.js`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [this.autoGiftItem()] }) });
      }
      // The bar completes, then the drawer is fetched and opens. Sections bundled with the add
      // can render before a brand-new cart exists (the first add of a session), so they are fetched afterwards.
      ended = true;
      Theme.loading.end({ immediate: true });
      await this.refresh();
      document.dispatchEvent(new CustomEvent('cart:change', { detail: { added: data } }));
      return data;
    } finally {
      if (!ended) Theme.loading.end();
    }
  },

  async change(line, quantity) {
    let data = await this.request(`${settings.routes.cartChange}.js`, { id: line, quantity });
    // Only gift lines left: the free item goes too, before anything re-renders.
    const autoGift = data.items.find((item) => item.properties?._gift === 'auto');
    if (autoGift && data.items.every((item) => item.properties?._gift)) {
      data = await this.request(`${settings.routes.cartUpdate}.js`, { updates: { [autoGift.key]: 0 } });
    }
    if (this.onCartPage) return location.reload();
    await this.applyUpdate(data.sections);
    document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: data } }));
    return data;
  },

  async updateNote(note) {
    await fetch(`${settings.routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note }),
    });
  },

  async applyDiscounts(codes) {
    const data = await this.request(`${settings.routes.cartUpdate}.js`, { discount: codes.join(',') });
    if (this.onCartPage) return location.reload();
    await this.applyUpdate(data.sections);
    document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: data } }));
    return data;
  },

  async refresh() {
    const ids = this.sectionsToRender();
    const sections = ids.length ? await (await fetch(`${location.pathname}?sections=${ids.join(',')}`)).json() : null;
    await this.applyUpdate(sections);
  },

  async applyUpdate(sections) {
    if (sections) {
      const drawer = this.drawer;
      const html = drawer && sections[drawer.dataset.sectionId];
      if (html) await this.renderDrawer(html);
      document.querySelectorAll('announcement-bar').forEach((bar) => bar.renderFreeShipping?.(sections));
    }
    const cart = await (await fetch(`${settings.routes.cart}.js`)).json();
    this.updateCount(cart.item_count);
    this.cart = cart;
  },

  async renderDrawer(html) {
    const drawer = this.drawer;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-panel]');
    const panel = drawer?.querySelector('[data-cart-panel]');
    if (!fresh || !panel) return;
    const wasEmpty = !panel.querySelector('[data-cart-footer]');
    const nowEmpty = !fresh.querySelector('[data-cart-footer]');
    const animate = drawer.isOpen && !wasEmpty && nowEmpty && !reducedMotion();
    if (animate) {
      const body = panel.querySelector('[data-cart-body]');
      const footer = panel.querySelector('[data-cart-footer]');
      await Promise.all([
        body?.animate({ opacity: [1, 0] }, { duration: 150, easing: 'ease-in', fill: 'forwards' }).finished,
        footer?.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(30px)' }], { duration: 150, easing: 'ease-in', fill: 'forwards' }).finished,
      ]);
    }
    const scroll = panel.querySelector('[data-cart-body]')?.scrollTop || 0;
    panel.replaceChildren(...fresh.childNodes);
    Theme.tidyProse(panel);
    const body = panel.querySelector('[data-cart-body]');
    if (body) body.scrollTop = scroll;
    if (animate) {
      body?.animate([{ opacity: 0, transform: 'translateY(30px)' }, { opacity: 1, transform: 'none' }], { duration: 250, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' });
    }
  },

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => (el.textContent = count));
    document.querySelectorAll('[data-cart-dot]').forEach((el) => el.classList.toggle('is-visible', count > 0));
  },

  open() {
    const drawer = this.drawer;
    if (drawer && settings.cartType !== 'page') drawer.show();
  },
});

document.addEventListener('cart:refresh', () => Cart.refresh());

/* Free gifts in the cart drawer (snippets/cart-gifts.liquid): adds a picked gift with its _gift property, and
   removes gifts again when the cart has dropped below the goal (the server lists those lines in data-remove). */
class CartGifts extends ThemeElement {
  setup() {
    this.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-gift-form]');
      if (!form) return;
      event.preventDefault();
      form.querySelector('button[type="submit"]')?.setAttribute('disabled', '');
      try {
        await Cart.add(new FormData(form));
      } catch (error) {
        form.querySelector('button[type="submit"]')?.removeAttribute('disabled');
      }
    });
  }

  // One request removes every gift line; the re-rendered drawer no longer lists them, so this runs once.
  async connected() {
    const keys = (this.dataset.remove || '').split(',').filter(Boolean);
    if (!keys.length) return;
    const data = await Cart.request(`${settings.routes.cartUpdate}.js`, { updates: Object.fromEntries(keys.map((key) => [key, 0])) });
    if (Cart.onCartPage) return location.reload();
    await Cart.applyUpdate(data.sections);
    document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: data } }));
  }
}
define('cart-gifts', CartGifts);

/* "Email me when it's back" (snippets/back-in-stock.liquid). Klaviyo mode posts a back in stock subscription;
   Shopify mode submits the customer form normally and reopens its dialog with the confirmation afterwards. */
class BackInStockForm extends ThemeElement {
  setup() {
    const form = this.querySelector('form');
    if (this.dataset.formId && new URLSearchParams(location.search).get('customer_posted') === 'true' && location.hash === `#${this.dataset.formId}`) {
      form.querySelectorAll('.form-control, button[type="submit"]').forEach((el) => (el.hidden = true));
      form.querySelector('[data-success]').hidden = false;
      this.closest('x-dialog')?.show?.();
    }
    if (!this.dataset.key) return;
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const email = form.querySelector('input[type="email"]');
      if (!email.reportValidity()) return;
      const show = (name) => form.querySelectorAll('[data-success], [data-error]').forEach((el) => (el.hidden = !el.matches(`[data-${name}]`)));
      try {
        const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.dataset.key)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: { channels: ['EMAIL'], profile: { data: { type: 'profile', attributes: { email: email.value } } } },
              relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.dataset.variantId}` } } },
            },
          }),
        });
        show(response.ok ? 'success' : 'error');
      } catch (error) {
        show('error');
      }
    });
  }
}
define('back-in-stock-form', BackInStockForm);

/* Product forms and quick-add forms */
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const isProductForm = form.matches('[data-product-form], [data-quick-add-form]');
  if (!isProductForm) return;
  if (settings.cartType === 'page' && !form.matches('[data-quick-add-form]')) return;
  event.preventDefault();
  const submitters = [...form.querySelectorAll('[type="submit"]'), ...document.querySelectorAll(`[type="submit"][form="${form.id}"]`)];
  if (submitters.some((b) => b.getAttribute('aria-busy') === 'true')) return;
  submitters.forEach((b) => b.setAttribute('aria-busy', 'true'));
  const insideDrawer = Boolean(form.closest('#cart-drawer'));
  try {
    const data = await Cart.add(new FormData(form));
    form.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: data }));
    Theme.announce(settings.strings.addedToCart.replace('{{ product }}', data.product_title || ''));
    if (settings.cartType === 'page' && !insideDrawer) return (location.href = settings.routes.cart);
    if (settings.cartType === 'drawer' || insideDrawer || form.matches('[data-quick-add-form]')) {
      form.closest('x-dialog:not(#cart-drawer)')?.hide();
      Cart.open();
    }
  } catch (error) {
    form.dispatchEvent(new CustomEvent('product:error', { bubbles: true, detail: error }));
    Theme.announce(error.description || error.message || settings.strings.cartError, true);
    Cart.refresh();
  } finally {
    submitters.forEach((b) => b.removeAttribute('aria-busy'));
  }
});

/* Line quantity, remove, discount removal, notes (delegated so re-rendered drawers keep working) */
const lineError = (key, message) => {
  document.querySelectorAll(`[data-line-key="${CSS.escape(key)}"] .line-item__error, [data-line-quantity="${CSS.escape(key)}"] ~ .line-item__error`).forEach((el) => {
    el.textContent = message;
    el.hidden = false;
  });
};

const changeLine = async (key, quantity, selector) => {
  selector?.setAttribute('aria-busy', 'true');
  try {
    await Cart.change(key, quantity);
  } catch (error) {
    const input = selector?.querySelector('input');
    if (input) input.value = input.dataset.previous;
    lineError(key, error.description || error.message || settings.strings.cartError);
  } finally {
    selector?.removeAttribute('aria-busy');
  }
};

document.addEventListener('change', (event) => {
  const input = event.target;
  const selector = input.closest?.('[data-line-quantity]');
  if (selector && input.matches('input')) {
    changeLine(selector.dataset.lineQuantity, Number(input.value), selector);
    return;
  }
  if (input.matches?.('[data-cart-note]')) Cart.updateNote(input.value);
  if (input.matches?.('[data-discount-input]')) applyDiscountField(input);
});

document.addEventListener('click', (event) => {
  const remove = event.target.closest('[data-line-remove]');
  if (remove) {
    event.preventDefault();
    changeLine(remove.dataset.lineRemove, 0, document.querySelector(`[data-line-quantity="${CSS.escape(remove.dataset.lineRemove)}"]`));
    return;
  }
  const removeDiscount = event.target.closest('[data-remove-discount]');
  if (removeDiscount) {
    event.preventDefault();
    const code = removeDiscount.dataset.removeDiscount;
    const codes = (Cart.cart?.discount_codes || []).map((d) => d.code).filter((c) => c !== code);
    Cart.applyDiscounts(codes);
    return;
  }
  const noteToggle = event.target.closest('[data-note-toggle]');
  if (noteToggle) {
    const drawer = noteToggle.closest('.cart-drawer');
    const panel = drawer?.querySelector('[data-note-panel]');
    if (!panel) return;
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add('is-open'));
    drawer.classList.add('is-note-open');
    noteToggle.setAttribute('aria-expanded', 'true');
    panel.querySelector('textarea')?.focus({ preventScroll: true });
    return;
  }
  const noteDrawer = event.target.closest('.cart-drawer.is-note-open');
  // "Save note", or a click on the dimmed drawer around the panel, closes it.
  if (noteDrawer && (event.target.closest('[data-note-save]') || !event.target.closest('[data-note-panel]'))) closeNote(noteDrawer);
});

// Closing needs no save of its own: moving focus off the textarea fires its change event, which saves the note.
function closeNote(drawer) {
  const panel = drawer.querySelector('[data-note-panel]');
  panel.classList.remove('is-open');
  drawer.classList.remove('is-note-open');
  const toggle = drawer.querySelector('[data-note-toggle]');
  toggle?.setAttribute('aria-expanded', 'false');
  toggle?.focus({ preventScroll: true });
  setTimeout(() => (panel.hidden = true), 200);
}

// Escape closes an open note panel before it closes the drawer (captured, so it runs ahead of the drawer).
document.addEventListener('keydown', (event) => {
  const drawer = event.key === 'Escape' && document.querySelector('.cart-drawer.is-note-open');
  if (!drawer) return;
  event.preventDefault();
  closeNote(drawer);
}, true);

async function applyDiscountField(input) {
  const code = input.value.trim();
  if (!code) return;
  const field = input.closest('.cart-discount');
  const spinner = field?.querySelector('.cart-discount__spinner');
  const error = field?.querySelector('.cart-discount__error');
  if (spinner) spinner.hidden = false;
  field?.setAttribute('aria-busy', 'true');
  try {
    if (!Cart.cart) Cart.cart = await (await fetch(`${settings.routes.cart}.js`)).json();
    const existing = (Cart.cart.discount_codes || []).map((d) => d.code);
    const data = await Cart.request(`${settings.routes.cartUpdate}.js`, { discount: [...existing, code].join(',') });
    const applied = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
    if (!applied || !applied.applicable) {
      if (error) error.hidden = false;
      if (spinner) spinner.hidden = true;
      // Keep only the codes that still apply
      await Cart.request(`${settings.routes.cartUpdate}.js`, { discount: existing.join(',') });
      return;
    }
    if (Cart.onCartPage) return location.reload();
    await Cart.applyUpdate(data.sections);
    document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: data } }));
  } catch (e) {
    if (error) error.hidden = false;
  } finally {
    if (spinner) spinner.hidden = true;
    field?.removeAttribute('aria-busy');
  }
}

/* Cart drawer: footer entrance and cross-sell dots */
document.addEventListener('dialog:before-show', (event) => {
  if (event.target.id !== 'cart-drawer') return;
  const footer = event.target.querySelector('[data-cart-footer]');
  if (footer && !reducedMotion()) footer.style.opacity = '0';
});
document.addEventListener('dialog:after-show', (event) => {
  if (event.target.id !== 'cart-drawer') return;
  const footer = event.target.querySelector('[data-cart-footer]');
  if (!footer) return;
  footer.style.opacity = '';
  if (!reducedMotion()) {
    footer.animate([{ opacity: 0, transform: 'translateY(30px)' }, { opacity: 1, transform: 'none' }], { duration: 250, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' });
  }
});

/* Shipping estimator */
class ShippingEstimator extends ThemeElement {
  setup() {
    this.country = this.querySelector('[data-country-select]');
    this.province = this.querySelector('[data-province-select]');
    this.provinceWrapper = this.querySelector('[data-province-wrapper]');
    this.results = this.querySelector('[data-results]');
    const def = this.dataset.defaultCountry;
    if (def) {
      const option = [...this.country.options].find((o) => o.value === def || o.textContent.trim() === def);
      if (option) this.country.value = option.value;
    }
    this.country.addEventListener('change', () => this.updateProvinces());
    this.updateProvinces(this.dataset.defaultProvince);
    this.querySelector('[data-estimate]').addEventListener('click', (e) => this.estimate(e.currentTarget));
  }

  updateProvinces(preselect) {
    const option = this.country.selectedOptions[0];
    const provinces = JSON.parse(option?.dataset.provinces || '[]');
    this.province.innerHTML = provinces.map(([value, label]) => `<option value="${value}">${label}</option>`).join('');
    if (preselect) this.province.value = preselect;
    this.provinceWrapper.hidden = provinces.length === 0;
  }

  async estimate(button) {
    button.setAttribute('aria-busy', 'true');
    Theme.loading.start();
    const params = new URLSearchParams({
      'shipping_address[country]': this.country.value,
      'shipping_address[province]': this.provinceWrapper.hidden ? '' : this.province.value,
      'shipping_address[zip]': this.querySelector('[data-zip-input]').value,
    });
    const s = settings.strings;
    try {
      const prepare = await fetch(`${settings.routes.cart}/prepare_shipping_rates.json?${params}`, { method: 'POST' });
      if (!prepare.ok) throw await prepare.json();
      let rates = null;
      // Shopify computes rates asynchronously: poll every 500ms, giving up after 10 seconds.
      for (let i = 0; i < 20 && rates === null; i++) {
        const response = await fetch(`${settings.routes.cart}/async_shipping_rates.json?${params}`);
        const data = await response.json();
        if (response.status === 200 && data.shipping_rates) rates = data.shipping_rates;
        else if (response.status !== 202) throw data;
        else await Theme.wait(500);
      }
      rates = rates || [];
      const items = rates.map((r) => `<li>${r.presentment_name || r.name}: ${Theme.formatMoney(r.price)}</li>`).join('');
      if (!rates.length) this.results.innerHTML = `<p>${s.shippingEstimateNone}</p>`;
      else this.results.innerHTML = `<p>${rates.length === 1 ? s.shippingEstimateOne : s.shippingEstimateMany}</p><ul>${items}</ul>`;
    } catch (error) {
      const messages = Object.entries(error || {}).map(([k, v]) => `<li>${k} ${[].concat(v).join(', ')}</li>`).join('');
      this.results.innerHTML = `<p>${s.shippingEstimateError}</p><ul>${messages}</ul>`;
    } finally {
      this.results.hidden = false;
      button.removeAttribute('aria-busy');
      Theme.loading.end();
    }
  }
}
define('shipping-estimator', ShippingEstimator);

/* ===========================================================================
   Product: variant changes, gallery, lightbox, sticky column and bar, quick buy
=========================================================================== */
const REFRESH_TYPES = ['sku', 'badges', 'quantity_selector', 'volume_pricing', 'price', 'payment_terms', 'variant_picker', 'inventory', 'buy_buttons', 'pickup_availability', 'liquid', 'info_row', 'feature_with_icon'];
const sectionCache = new Map();

// Section HTML, cached. Variant changes load silently (the page already shows the change); `progress` shows the top loading bar.
/* A page shown in a modal (a size chart, an info popup) can carry its own script, which markup parsed or set through
   innerHTML never runs. Fresh script elements run as soon as they are in the document, or when the root is added. */
const runModalScripts = (root) => {
  root.querySelectorAll('.modal__body script:not([type="application/json"])').forEach((parsed) => {
    const script = document.createElement('script');
    [...parsed.attributes].forEach((attribute) => script.setAttribute(attribute.name, attribute.value));
    script.textContent = parsed.textContent;
    parsed.replaceWith(script);
  });
};

const fetchHtml = async (url, { progress = false } = {}) => {
  if (sectionCache.has(url)) return sectionCache.get(url);
  const promise = (progress ? Theme.fetch(url) : fetch(url)).then((r) => r.text());
  sectionCache.set(url, promise);
  if (sectionCache.size > 100) sectionCache.delete(sectionCache.keys().next().value);
  promise.catch(() => sectionCache.delete(url));
  return promise;
};

class ProductSection extends ThemeElement {
  setup() {
    // Listened for on the document: a dropdown's options sit in a popover that moves to <body> on phones.
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!belongsTo(this, input) || !input.closest('variant-picker, .variant-picker__popover') || !input.matches('input[type="radio"]')) return;
      if (input.hasAttribute('data-close-popover')) input.closest('x-dialog')?.hide();
      if (input.dataset.productUrl && this.dataset.updateUrl !== undefined && this.hasAttribute('data-update-url') && !this.closest('.quick-buy')) {
        location.href = input.dataset.productUrl;
        return;
      }
      this.onOptionChange(input);
    });
    this.addEventListener('pointerover', (event) => {
      const label = event.target.closest('variant-picker label[for]');
      if (!label) return;
      const input = this.querySelector(`#${CSS.escape(label.htmlFor)}`);
      if (input && !input.checked) fetchHtml(this.buildUrl(this.selectedValues(input)));
    });
    if (this.hasAttribute('data-update-url') && !this.closest('.quick-buy')) this.trackRecentlyViewed();
    // product-gallery is defined further down this file, so wait until its methods exist.
    customElements.whenDefined('product-gallery').then(() => this.applyMediaFilter());
  }

  get formId() {
    return this.dataset.formId;
  }

  selectedValues(changed) {
    const values = [];
    this.querySelectorAll('variant-picker fieldset[data-option-position]').forEach((fieldset) => {
      const position = fieldset.dataset.optionPosition;
      let checked = fieldset.querySelector('input[type="radio"]:checked') || document.querySelector(`input[type="radio"][form="${this.formId}"][data-option-position="${position}"]:checked`);
      if (changed && changed.dataset.optionPosition === position) checked = changed;
      if (checked) values.push(checked.value);
    });
    this.querySelectorAll('variant-picker input.sr-only[data-option-position]:checked').forEach((hidden) => {
      if (!values.includes(hidden.value)) values.unshift(hidden.value);
    });
    return values;
  }

  buildUrl(values, productUrl = this.dataset.productUrl) {
    const url = new URL(productUrl, location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('option_values', values.join(','));
    return url.toString();
  }

  async onOptionChange(input) {
    const focusId = input.id;
    const productUrl = input.dataset.productUrl || this.dataset.productUrl;
    const html = await fetchHtml(this.buildUrl(this.selectedValues(input), productUrl));
    const doc = new DOMParser().parseFromString(this.closest('.quick-buy') ? QuickBuy.rewriteIds(html) : html, 'text/html');
    const fresh = doc.querySelector('product-section');
    if (!fresh) return;

    if (productUrl !== this.dataset.productUrl) {
      // Combined listing: another product replaces this whole area
      this.replaceWith(fresh);
      return;
    }

    const quantity = this.querySelector('[data-product-quantity] input')?.value;
    // Only blocks whose markup changed are replaced: rewriting an unchanged one reloads its images (flags, logos)
    // and makes it blink on every size change.
    this.querySelectorAll('.product-info__block[data-block-id]').forEach((block) => {
      if (!REFRESH_TYPES.includes(block.dataset.blockType)) return;
      const next = fresh.querySelector(`.product-info__block[data-block-id="${block.dataset.blockId}"]`);
      if (next && next.innerHTML !== block.innerHTML) {
        block.innerHTML = next.innerHTML;
        runModalScripts(block);
      }
    });
    const qty = this.querySelector('[data-product-quantity] input');
    if (qty && quantity) qty.value = quantity;
    this.querySelectorAll('form[hidden] [data-variant-id]').forEach((el) => {
      const next = fresh.querySelector('form[hidden] [data-variant-id]');
      if (next) el.value = next.value;
    });

    const variantId = fresh.querySelector('form [name="id"][data-variant-id]')?.value;
    // Move the gallery only when the new variant has its own image and it differs from the current one
    // (choosing another size of the same colour keeps the shopper where they are)
    const gallery = this.querySelector('product-gallery');
    const variantMedia = fresh.querySelector('product-gallery')?.dataset.variantMedia || '';
    const selectedMedia = variantMedia && variantMedia !== gallery?.dataset.variantMedia ? variantMedia : '';
    if (gallery) gallery.dataset.variantMedia = variantMedia;
    const sticky = this.querySelector('sticky-add-to-cart');
    const freshSticky = fresh.querySelector('sticky-add-to-cart');
    if (sticky && freshSticky) {
      sticky.querySelector('[data-sticky-price]').innerHTML = freshSticky.querySelector('[data-sticky-price]').innerHTML;
      const btn = sticky.querySelector('[data-sticky-button]');
      const freshBtn = freshSticky.querySelector('[data-sticky-button]');
      btn.outerHTML = freshBtn.outerHTML;
      const img = sticky.querySelector('[data-sticky-image]');
      const freshImg = freshSticky.querySelector('[data-sticky-image]');
      if (img && freshImg) img.innerHTML = freshImg.innerHTML;
    }

    if (this.hasAttribute('data-update-url') && !this.closest('.quick-buy') && variantId) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variantId);
      history.replaceState({}, '', url);
    }

    this.applyMediaFilter();
    if (selectedMedia) gallery?.goToMedia(selectedMedia);
    this.querySelector(`#${CSS.escape(focusId)}`)?.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variantId } }));
  }

  /* Variant image sets: images whose alt ends with "#Option_Value" belong to that value */
  applyMediaFilter() {
    const gallery = this.querySelector('product-gallery');
    if (!gallery || gallery.dataset.hasVariants !== 'true') return;
    const selected = {};
    this.querySelectorAll('variant-picker fieldset[data-option-position]').forEach((fieldset) => {
      const name = fieldset.querySelector('.variant-picker__name')?.textContent.trim().toLowerCase();
      const checked = fieldset.querySelector('input:checked');
      const label = checked && fieldset.querySelector(`label[for="${CSS.escape(checked.id)}"]`);
      const value = (label?.textContent || checked?.closest('.variant-picker__dropdown')?.querySelector('[data-selected-value]')?.textContent || '').trim().toLowerCase();
      if (name) selected[name] = value;
    });
    gallery.filterMedia(selected);
  }

  trackRecentlyViewed() {
    try {
      const id = Number(this.dataset.productId);
      const list = JSON.parse(localStorage.getItem('brandlabs:recently-viewed') || '[]').filter((x) => x !== id);
      list.unshift(id);
      localStorage.setItem('brandlabs:recently-viewed', JSON.stringify(list.slice(0, 20))); // newest 20 products
    } catch (e) {}
  }
}
define('product-section', ProductSection);

/* Gallery */
class ProductGallery extends ThemeElement {
  setup() {
    this.carousel = this.querySelector('x-carousel');
    if (!this.carousel) return;
    // Parents set up before their children when HTML is inserted (quick buy), so finish the carousel first.
    customElements.upgrade(this.carousel);
    // The frame around the track takes the height of the image on screen. The track itself keeps one height (its
    // tallest image): resizing a scroll container makes the browser re-snap it, which shows as a wobble of the
    // images and the progress bar whenever two images differ in shape. The frame follows the image the carousel
    // rests on: it keeps its height during a swipe and eases to the next image's height once the swipe settles.
    this.viewport = this.carousel.closest('.gallery__viewport');
    this.restingIndex = this.carousel.index;
    this.carousel.addEventListener('carousel:change', (event) => this.onChange(event.detail));
    this.carousel.addEventListener('carousel:settle', ({ detail }) => {
      this.restingIndex = detail.index;
      this.updateHeight();
    });
    this.addEventListener('click', (event) => {
      const zoomBtn = event.target.closest('[data-zoom-button]');
      if (zoomBtn) return this.openLightbox(this.currentImageIndex());
      const media = event.target.closest('.gallery__media--image');
      if (media && this.hasAttribute('data-zoom') && isDesktop() && !media.hasAttribute('data-clone')) {
        this.openLightbox(this.imageCells().indexOf(media));
      }
    });
    this.addEventListener('lightbox:open', (event) => this.openLightbox(event.detail.index));
    this.updateHeight();
    // Images that size late (quick buy, lazy loading) still resize the frame.
    const resize = new ResizeObserver(() => this.updateHeight());
    this.carousel.cells.forEach((cell) => resize.observe(cell));
    this.setupGridSync();
    const selected = this.carousel.querySelector('.gallery__media.is-selected');
    if (selected) requestAnimationFrame(() => this.syncControls(selected.dataset.mediaId));
    this.setupPinch();
  }

  get isGridLayout() {
    return isDesktop() && this.dataset.layout.startsWith('grid');
  }

  imageCells() {
    return this.carousel.cells.filter((c) => c.dataset.mediaType === 'image');
  }

  currentImageIndex() {
    const current = this.carousel.cells[this.carousel.index];
    return Math.max(0, this.imageCells().indexOf(current));
  }

  onChange({ cell }) {
    if (!cell) return;
    this.querySelectorAll('deferred-video, deferred-model').forEach((m) => !cell.contains(m) && m.pause?.());
    this.classList.toggle('is-media-current', cell.dataset.mediaType !== 'image');
    this.syncControls(cell.dataset.mediaId);
    if (this.hasAttribute('data-autoplay')) cell.querySelector('deferred-video')?.play?.(true);
    [cell.previousElementSibling, cell.nextElementSibling].forEach((n) => n?.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager')));
  }

  syncControls(mediaId) {
    this.querySelectorAll('[data-carousel="dot"][data-media-id]').forEach((dot) => dot.setAttribute('aria-current', String(dot.dataset.mediaId === mediaId)));
    const thumb = this.querySelector(`.gallery__thumb[data-media-id="${mediaId}"]`);
    const scroller = thumb?.parentElement;
    if (thumb && scroller) {
      const vertical = scroller.scrollHeight > scroller.clientHeight + 2 && getComputedStyle(scroller).flexDirection === 'column';
      if (vertical) scroller.scrollTo({ top: thumb.offsetTop - scroller.clientHeight / 2 + thumb.clientHeight / 2, behavior: 'smooth' });
      else scroller.scrollTo({ left: thumb.offsetLeft - scroller.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
    }
  }

  updateHeight() {
    if (!this.viewport) return;
    if (this.isGridLayout) {
      this.viewport.style.height = '';
      return;
    }
    // A slide whose image has not loaded yet has no height: keep the current one rather than collapse the frame.
    // The slide's ResizeObserver brings the height once it loads.
    const height = this.carousel.cells[this.restingIndex]?.offsetHeight;
    if (height) this.viewport.style.height = `${height}px`;
  }

  goToMedia(mediaId) {
    if (!this.carousel) return;
    const cells = this.carousel.cells;
    const index = cells.findIndex((c) => c.dataset.mediaId === mediaId);
    if (index < 0) return;
    if (this.isGridLayout) {
      cells[index].scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start', inline: 'nearest' });
    } else {
      this.carousel.select(index, 'auto');
    }
    this.syncControls(mediaId);
  }

  /* Grid layouts: thumbnails scroll the page and follow the visible media */
  setupGridSync() {
    this.addEventListener(
      'click',
      (event) => {
        const control = event.target.closest('[data-carousel="dot"][data-media-id]');
        if (!control || !this.isGridLayout) return;
        event.stopImmediatePropagation();
        this.goToMedia(control.dataset.mediaId);
      },
      true
    );
    const observer = new IntersectionObserver(
      (entries) => {
        if (!this.isGridLayout) return;
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) this.syncControls(visible.target.dataset.mediaId);
      },
      { threshold: 0.5 }
    );
    this.carousel.querySelectorAll('.gallery__media').forEach((cell) => observer.observe(cell));
  }

  filterMedia(selected) {
    if (!this.carousel) return;
    const title = this.dataset.productTitle;
    const current = this.carousel.querySelector('.gallery__media.is-selected')?.dataset.mediaId;
    let changed = false;
    this.carousel.querySelectorAll('.gallery__media:not([data-clone])').forEach((cell) => {
      const alt = cell.dataset.alt || '';
      const match = alt !== title && alt.match(/#([^#]+)_([^#]+)$/);
      let hide = false;
      if (match) {
        const name = match[1].trim();
        const value = match[2].trim();
        if (name in selected && selected[name] !== value) hide = true;
      }
      if (cell.dataset.mediaId === current) hide = false;
      if (cell.hidden !== hide) changed = true;
      cell.hidden = hide;
      this.querySelectorAll(`[data-media-id="${cell.dataset.mediaId}"]:not(.gallery__media)`).forEach((c) => (c.hidden = hide));
    });
    const firstVisible = this.carousel.querySelector('.gallery__media:not([hidden]):not([data-clone])');
    this.carousel.querySelectorAll('.gallery__media').forEach((c) => c.classList.toggle('is-first-visible', c === firstVisible));
    if (changed) this.carousel.dispatchEvent(new CustomEvent('carousel:filter', { bubbles: true }));
  }

  setupPinch() {
    if (!/iPhone|iPad|iPod/.test(navigator.userAgent) || !this.hasAttribute('data-zoom')) return;
    this.carousel.addEventListener('gestureend', (event) => {
      if (event.scale > 1.5) this.openLightbox(this.currentImageIndex());
    });
  }

  openLightbox(index) {
    const images = this.imageCells().map((cell) => ({
      src: cell.dataset.zoomSrc,
      width: Number(cell.dataset.zoomWidth),
      height: Number(cell.dataset.zoomHeight),
      thumb: cell.querySelector('img'),
    }));
    if (!images.length) return;
    Lightbox.open(images, Math.max(0, index), Number(this.dataset.zoom) || 3, Boolean(this.querySelector('gallery-loop')));
  }
}
define('product-gallery', ProductGallery);

/* Lightbox: full-screen image viewer with zoom and pan */
/* Full-screen gallery. The track is the theme's own <x-carousel>; with `loop` a <gallery-loop> clones the first and
   last slide, so swiping and the arrows both wrap around endlessly, as in the product gallery. */
const Lightbox = {
  open(images, index, maxZoom, loop = false) {
    this.close(true);
    const s = settings.strings || {};
    const el = document.createElement('div');
    el.className = 'lightbox color-surface';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.tabIndex = -1;
    el.innerHTML = `
      <x-carousel id="lightbox-track" class="lightbox__track scroll-area" cell-selector=":scope > .lightbox__slide:not([data-clone])"${loop ? ' loop' : ''}>${images.map((img, i) => `<div class="lightbox__slide" data-index="${i}"><img src="${img.thumb?.currentSrc || img.src}" width="${img.width}" height="${img.height}" alt="" draggable="false"></div>`).join('')}</x-carousel>
      ${loop && images.length > 1 ? '<gallery-loop carousel="lightbox-track" cells=".lightbox__slide" desktop></gallery-loop>' : ''}
      <div class="lightbox__controls">
        ${images.length > 1 ? `<button type="button" class="circle-button circle-button--animate-x is-prev" data-lb="prev" aria-label="${s.previous || 'Previous'}">${Lightbox.icon('left')}</button>` : ''}
        <button type="button" class="circle-button circle-button--lg circle-button--animate-y" data-lb="close" aria-label="${s.closeGallery || 'Close gallery'}"><svg viewBox="0 0 16 16" width="16" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true"><path d="m1 1 14 14M15 1 1 15"/></svg></button>
        ${images.length > 1 ? `<button type="button" class="circle-button circle-button--animate-x" data-lb="next" aria-label="${s.next || 'Next'}">${Lightbox.icon('right')}</button>` : ''}
      </div>`;
    document.body.append(el);
    this.el = el;
    this.maxZoom = maxZoom;
    this.track = el.querySelector('.lightbox__track');
    this.images = images;
    this.opener = document.activeElement;
    Theme.lockScroll(el);
    requestAnimationFrame(() => {
      this.track.select(index, 'auto');
      el.classList.add('is-open');
      el.focus({ preventScroll: true });
      this.zoomFromThumb(el, index);
      this.loadFull(index);
    });
    this.track.addEventListener('scroll', () => this.loadFull(this.current()), { passive: true });
    el.addEventListener('click', (e) => {
      const action = e.target.closest('[data-lb]')?.dataset.lb;
      if (action === 'close') return this.close();
      if (action === 'prev') return this.go(-1);
      if (action === 'next') return this.go(1);
    });
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowLeft') this.go(-1);
      if (e.key === 'ArrowRight') this.go(1);
    });
    this.setupZoom();
  },

  /* Animate slide `index` of lightbox `el` from its thumbnail on the page (or back to it when
     `reverse`). When the thumbnail is out of view the image fades instead. */
  zoomFromThumb(el, index, reverse = false) {
    const img = el.querySelectorAll('.lightbox__slide:not([data-clone]) img')[index];
    if (!img || reducedMotion()) return;
    const from = this.images[index]?.thumb?.getBoundingClientRect();
    const timing = { duration: 333, easing: 'cubic-bezier(0.4, 0, 0.22, 1)', direction: reverse ? 'reverse' : 'normal', fill: reverse ? 'forwards' : 'none' };
    if (!from || from.width === 0 || from.bottom < 0 || from.top > innerHeight) {
      return img.animate({ opacity: [0, 1] }, timing);
    }
    const to = img.getBoundingClientRect();
    const scale = from.width / to.width;
    const x = from.left - to.left + (from.width - to.width) / 2;
    const y = from.top - to.top + (from.height - to.height) / 2;
    return img.animate([{ transform: `translate(${x}px, ${y}px) scale(${scale})` }, { transform: 'none' }], timing);
  },

  /* Slides start with the image already loaded on the page (no blank flash), then switch to the zoom
     size once it has decoded: the given slide and its neighbours. */
  loadFull(index) {
    const slides = this.el?.querySelectorAll('.lightbox__slide:not([data-clone]) img') || [];
    [index - 1, index, index + 1].forEach((i) => {
      const slide = slides[i];
      const full = this.images[i]?.src;
      if (!slide || !full || slide.dataset.full) return;
      slide.dataset.full = 'loading';
      const preload = new Image();
      preload.src = full;
      preload.decode().then(() => (slide.src = full), () => {});
    });
  },

  icon(dir) {
    return dir === 'left'
      ? '<svg class="icon icon--direction-aware" viewBox="0 0 16 18" width="12" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true"><path d="m12 1-8 8 8 8"/></svg>'
      : '<svg class="icon icon--direction-aware" viewBox="0 0 16 18" width="12" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true"><path d="m4 1 8 8-8 8"/></svg>';
  },

  current() {
    return this.track.index || 0;
  },

  go(delta) {
    this.resetZoom();
    if (delta > 0) this.track.next();
    else this.track.prev();
  },

  setupZoom() {
    let zoomed = null;
    let start = null;
    let pan = { x: 0, y: 0 };
    let lastTap = 0;
    // Keep a zoomed image covering the viewport: it cannot be dragged past its own edges.
    const clamp = (img, scale, x, y) => {
      const w = img.offsetWidth * scale;
      const h = img.offsetHeight * scale;
      const mx = Math.max(0, (w - this.track.clientWidth) / 2);
      const my = Math.max(0, (h - this.track.clientHeight) / 2);
      return { x: Math.min(mx, Math.max(-mx, x)), y: Math.min(my, Math.max(-my, y)) };
    };
    const apply = (img, scale, x, y) => {
      const p = scale > 1 ? clamp(img, scale, x, y) : { x: 0, y: 0 };
      if (img === zoomed) pan = p;
      img.style.transform = `translate(${p.x}px, ${p.y}px) scale(${scale})`;
    };
    const toggle = (img, clientX, clientY) => {
      if (zoomed === img) {
        img.style.transition = 'transform 0.3s';
        apply(img, 1, 0, 0);
        img.parentElement.classList.remove('is-zoomed');
        zoomed = null;
        return;
      }
      const rect = img.getBoundingClientRect();
      const scale = this.maxZoom;
      const x = (rect.left + rect.width / 2 - clientX) * (scale - 1);
      const y = (rect.top + rect.height / 2 - clientY) * (scale - 1);
      zoomed = img;
      img.style.transition = 'transform 0.3s';
      apply(img, scale, x, y);
      img.parentElement.classList.add('is-zoomed');
    };
    this.resetZoom = () => {
      if (zoomed) {
        apply(zoomed, 1, 0, 0);
        zoomed.parentElement.classList.remove('is-zoomed');
        zoomed = null;
      }
    };
    // While zoomed, a click anywhere zooms back out; otherwise a click on an image zooms into it.
    this.track.addEventListener('click', (e) => {
      const img = zoomed || e.target.closest('.lightbox__slide img');
      if (!img || this._panned) return;
      toggle(img, e.clientX, e.clientY);
    });
    this.track.addEventListener('pointerdown', (e) => {
      this._panned = false;
      if (!zoomed) return;
      start = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y, id: e.pointerId };
      zoomed.style.transition = 'none';
    });
    // The pointer is captured only once a drag starts, so a plain click still reaches the click handler.
    this.track.addEventListener('pointermove', (e) => {
      if (!zoomed || !start) return;
      if (!this._panned && Math.abs(e.clientX - start.x) + Math.abs(e.clientY - start.y) > 5) {
        this._panned = true;
        this.track.setPointerCapture(start.id);
      }
      pan = { x: start.px + e.clientX - start.x, y: start.py + e.clientY - start.y };
      apply(zoomed, this.maxZoom, pan.x, pan.y);
    });
    this.track.addEventListener('pointerup', () => {
      start = null;
      setTimeout(() => (this._panned = false));
    });
    this.track.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastTap < 300 && e.changedTouches[0]) {
        const t = e.changedTouches[0];
        const img = e.target.closest('.lightbox__slide img');
        if (img) toggle(img, t.clientX, t.clientY);
        e.preventDefault();
      }
      lastTap = now;
    });
    // Wheel and trackpad scrolling pan a zoomed image instead of moving between slides.
    this.track.addEventListener('wheel', (e) => {
      if (!zoomed) return;
      e.preventDefault();
      zoomed.style.transition = 'none';
      apply(zoomed, this.maxZoom, pan.x - e.deltaX, pan.y - e.deltaY);
    }, { passive: false });
    this.track.addEventListener('scroll', () => zoomed && !start && this.resetZoom(), { passive: true });
  },

  close(instant = false) {
    const el = this.el;
    if (!el) return;
    this.el = null;
    Theme.unlockScroll(el);
    if (instant) return el.remove();
    const index = this.current();
    this.resetZoom?.();
    this.zoomFromThumb(el, index, true);
    el.classList.remove('is-open');
    setTimeout(() => el.remove(), 333);
    this.opener?.focus?.({ preventScroll: true });
  },
};
Theme.Lightbox = Lightbox;

/* Inline Shopify-hosted video (video block, rich text and multi-column videos): plays muted once it scrolls into
   view when `autoplay` is set; otherwise shows its poster and starts with sound on tap. A block may hold a desktop
   and a mobile video; the visible one plays. */
class InlineVideo extends ThemeElement {
  setup() {
    this.autoplay = this.hasAttribute('autoplay');
    this.showControls = this.hasAttribute('show-controls');
    if (!this.autoplay) (this.querySelector('[data-play]') || this).addEventListener('click', () => this.playWithSound());
  }

  connected() {
    // Click-to-play videos start fetching metadata a screen ahead, so the first frame is ready on tap.
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      if (this.autoplay) this.playMuted();
      else this.querySelectorAll('video').forEach((video) => (video.preload = 'metadata'));
    }, { rootMargin: this.autoplay ? '0px' : '800px 0px' });
    this.observer.observe(this);
  }

  disconnected() {
    this.observer?.disconnect();
  }

  get video() {
    return [...this.querySelectorAll('video')].find((video) => video.getClientRects().length) || this.querySelector('video');
  }

  playMuted() {
    const video = this.video;
    if (!video || !video.paused) return;
    video.muted = true;
    video.play().catch(() => {
      // Autoplay refused (low power mode): show native controls until playback starts.
      video.controls = true;
      video.addEventListener('playing', () => (video.controls = this.showControls), { once: true });
    });
  }

  playWithSound() {
    const video = this.video;
    if (!video || this.classList.contains('is-playing')) return;
    video.muted = false;
    video.controls = this.showControls;
    this.classList.add('is-playing');
    video.play().catch(() => {});
  }
}
define('inline-video', InlineVideo);

/* Deferred videos and models */
class DeferredMedia extends ThemeElement {
  setup() {
    this.querySelector('[data-play]')?.addEventListener('click', () => this.play());
  }

  load() {
    if (this.loaded) return;
    this.loaded = true;
    const template = this.querySelector('template');
    this.append(template.content.cloneNode(true));
    this.classList.add('is-loaded');
    const video = this.querySelector('video');
    if (video) {
      if (!isDesktop() || this.closest('[data-autoplay]')) video.muted = true;
      video.play().catch(() => (video.controls = true));
    }
    const iframe = this.querySelector('iframe');
    if (iframe && (!isDesktop() || this.closest('[data-autoplay]'))) iframe.src += (iframe.src.includes('?') ? '&' : '?') + 'mute=1&muted=1';
    document.querySelectorAll('deferred-video.is-loaded').forEach((other) => other !== this && other.pause());
  }

  play(muted = false) {
    if (!this.loaded) return this.load();
    const video = this.querySelector('video');
    if (video) {
      if (muted) video.muted = true;
      video.play().catch(() => {});
    }
  }

  pause() {
    this.querySelector('video')?.pause();
    this.querySelector('iframe')?.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    this.querySelector('iframe')?.contentWindow?.postMessage('{"method":"pause"}', '*');
  }
}
define('deferred-video', DeferredMedia);
define('deferred-model', class extends DeferredMedia {
  load() {
    if (this.loaded) return;
    super.load();
    window.Shopify?.loadFeatures?.([{ name: 'model-viewer-ui', version: '1.0' }, { name: 'shopify-xr', version: '1.0' }], () => {
      const viewer = this.querySelector('model-viewer');
      if (viewer && window.Shopify.ModelViewerUI) new window.Shopify.ModelViewerUI(viewer);
    });
  }
});

/* Smart sticky column: pins to the top, or to the bottom when taller than the viewport */
class StickyColumn extends ThemeElement {
  setup() {
    this.onScroll = this.onScroll.bind(this);
    this.lastY = scrollY;
    this.top = 0;
    new ResizeObserver(() => this.onScroll()).observe(this);
  }

  connected() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnected() {
    window.removeEventListener('scroll', this.onScroll);
  }

  onScroll() {
    if (!isDesktop()) return (this.style.top = '');
    if (this._frame) return;
    this._frame = requestAnimationFrame(() => {
      this._frame = null;
      const resting = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-offset')) || 0) + 20;
      const height = this.offsetHeight;
      const viewport = innerHeight;
      if (height + resting <= viewport) {
        this.style.top = `${resting}px`;
      } else {
        const delta = scrollY - this.lastY;
        const minTop = viewport - height - 20;
        this.top = Math.max(minTop, Math.min(resting, (this.top || resting) - delta));
        this.style.top = `${this.top}px`;
      }
      this.lastY = scrollY;
    });
  }
}
define('sticky-column', StickyColumn);

/* Sticky add to cart bar */
class StickyAddToCart extends ThemeElement {
  // Shown once the buy buttons have scrolled above the viewport, hidden again near the footer. Positions are
  // read on scroll (not with an IntersectionObserver), so jumps such as "back to top" can never leave it stale.
  setup() {
    this.hidden = false;
    this.footer = document.querySelector('.bl-section--footer, .footer');
    this.onScroll = () => {
      if (this._frame) return;
      this._frame = requestAnimationFrame(() => {
        this._frame = null;
        this.update();
      });
    };
  }

  connected() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  disconnected() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  // The buy buttons block is looked up each time: variant changes re-render the form inside it.
  get buyButtons() {
    const form = document.getElementById(this.dataset.formId);
    return form?.closest('.product-info__block') || form;
  }

  update() {
    const buttons = this.buyButtons?.getBoundingClientRect();
    const passed = buttons ? buttons.bottom < 0 : false;
    const nearFooter = this.footer ? this.footer.getBoundingClientRect().top < innerHeight : false;
    this.classList.toggle('is-visible', passed && !nearFooter);
  }
}
define('sticky-add-to-cart', StickyAddToCart);

/* Complementary products: loaded after render */
class ComplementaryProducts extends ThemeElement {
  // Loaded with the page, so the block is in place before it scrolls into view and nothing jumps.
  async setup() {
    try {
      const html = await (await fetch(this.dataset.url)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`complementary-products[data-block-id="${this.dataset.blockId}"]`);
      if (!fresh || !fresh.children.length) return; // keep the empty block: its spacing still applies
      this.innerHTML = fresh.innerHTML;
      this.hidden = false;
    } catch (e) {
      /* leave the block empty */
    }
  }
}
define('complementary-products', ComplementaryProducts);

/* Gift card recipient */
define('gift-card-recipient', class extends ThemeElement {
  setup() {
    const toggle = this.querySelector('[data-recipient-toggle]');
    const fields = this.querySelector('[data-recipient-fields]');
    const counter = this.querySelector('[data-counter]');
    const message = this.querySelector('[data-counted]');
    this.querySelector('[data-offset]').value = new Date().getTimezoneOffset().toString();
    toggle.addEventListener('change', () => {
      fields.hidden = !toggle.checked;
      fields.querySelectorAll('input, textarea').forEach((input) => (input.disabled = !toggle.checked));
      fields.querySelector('[data-required]').required = toggle.checked;
    });
    fields.querySelectorAll('input, textarea').forEach((input) => (input.disabled = true));
    message.addEventListener('input', () => (counter.textContent = `${message.value.length}/200`));
  }
});

/* Share buttons: native share sheet where supported */
define('share-buttons', class extends ThemeElement {
  setup() {
    if (!navigator.share || !matchMedia('(pointer: coarse)').matches) return;
    this.classList.add('has-native');
    const btn = this.querySelector('.share-buttons__native');
    btn.hidden = false;
    btn.addEventListener('click', () => navigator.share({ title: this.dataset.title, url: this.dataset.url }).catch(() => {}));
  }
});

/* Variant picker: exposes the selected variant and a way to select a combination (used by the size guide). */
define('variant-picker', class extends ThemeElement {
  get selectedVariant() {
    try {
      return JSON.parse(this.querySelector('[data-selected-variant]')?.textContent || 'null');
    } catch (e) {
      return null;
    }
  }

  async selectCombination({ optionValues }) {
    let last = null;
    optionValues.forEach((value) => {
      const input = this.querySelector(`input[type="radio"][data-option-position][value="${value}"]`);
      if (input && !input.checked) {
        input.checked = true;
        last = input;
      }
    });
    if (last) await this.closest('product-section')?.onOptionChange(last);
  }
});

/* Product form feedback: message banners for cart_type = message, shown for 2.5 seconds */
const BANNER_TIME = 2500;
document.addEventListener('product:added', (event) => {
  const banner = event.target.closest('.buy-buttons')?.querySelector('[data-form-banner]');
  if (!banner || settings.cartType !== 'message') return;
  banner.className = 'buy-buttons__banner banner banner--success';
  banner.textContent = settings.strings.addedToCartMessage || 'Added to your cart!';
  banner.hidden = false;
  setTimeout(() => (banner.hidden = true), BANNER_TIME);
});
document.addEventListener('product:error', (event) => {
  const banner = event.target.closest('.buy-buttons')?.querySelector('[data-form-banner]');
  if (!banner) return;
  banner.className = 'buy-buttons__banner banner banner--error';
  banner.textContent = event.detail.description || event.detail.message || settings.strings.cartError;
  banner.hidden = false;
  setTimeout(() => (banner.hidden = true), BANNER_TIME);
});

/* ===========================================================================
   Quick buy modal (product cards and upsells)
=========================================================================== */
const QuickBuy = {
  rewriteIds(html) {
    return html.replace(/product-form-([\w-]+)/g, 'quick-product-form-$1').replace(/(?<![\w<\/-])gallery-(?!-)([\w-]+)/g, 'quick-gallery-$1').replace(/modal-([\w-]+)/g, 'quick-modal-$1');
  },

  async open(url, opener) {
    const page = new URL(url, location.origin);
    page.searchParams.delete('variant');
    const cacheKey = `quick:${page}`;
    let html = sectionCache.get(cacheKey);
    const fresh = !html || Date.now() - (html.time || 0) > 300000;
    if (fresh) {
      const text = await fetchHtml(page.toString(), { progress: true });
      html = { text, time: Date.now() };
      sectionCache.set(cacheKey, html);
    }
    const doc = new DOMParser().parseFromString(this.rewriteIds(html.text), 'text/html');
    const section = doc.querySelector('product-section');
    if (!section) return (location.href = url);
    const allowed = ['@app', 'vendor', 'title', 'price', 'quantity_selector', 'payment_terms', 'separator', 'variant_picker', 'line_item_property', 'buy_buttons'];
    section.querySelectorAll('.product-info__block[data-block-type]').forEach((b) => (!allowed.includes(b.dataset.blockType) || b.hasAttribute('data-page-only')) && b.remove());
    section.querySelectorAll('.gallery__media[data-clone], .main-product__below, sticky-add-to-cart, script[data-product-json], gallery-loop, product-gallery-progress, gallery-mobile-zoom, .gallery__zoom, .gallery__thumbs, .product-media-note').forEach((n) => n.remove());
    section.querySelectorAll('.product-info__group:empty').forEach((n) => n.remove());
    section.removeAttribute('data-update-url');
    section.querySelector('product-gallery')?.removeAttribute('data-zoom');
    const gallery = section.querySelector('product-gallery');
    if (gallery) {
      gallery.className = gallery.className.replace(/gallery--(grid\w*|carousel\w*)/g, 'gallery--carousel_dots');
      gallery.dataset.layout = 'carousel_dots';
      const carouselId = gallery.querySelector('x-carousel')?.id;
      if (carouselId && gallery.querySelectorAll('.gallery__media').length > 1) {
        gallery.querySelector('.gallery__viewport').insertAdjacentHTML('beforeend', `
          <button type="button" class="circle-button circle-button--animate-x is-prev gallery__arrow gallery__arrow--prev" aria-controls="${carouselId}" data-carousel="prev"><span class="sr-only">${settings.strings.previous || 'Previous'}</span>${Lightbox.icon('left')}</button>
          <button type="button" class="circle-button circle-button--animate-x gallery__arrow gallery__arrow--next" aria-controls="${carouselId}" data-carousel="next"><span class="sr-only">${settings.strings.next || 'Next'}</span>${Lightbox.icon('right')}</button>`);
        // The product page may use thumbnails; the modal always shows dots under the one-up carousel.
        let dots = gallery.querySelector('.gallery__dots');
        if (!dots) {
          const cells = [...gallery.querySelectorAll('.gallery__media')];
          gallery.querySelector('.gallery__viewport').insertAdjacentHTML('afterend', `<div class="carousel-dots gallery__dots">${cells.map((cell, i) => `<button type="button" aria-controls="${carouselId}" data-carousel="dot" data-index="${i}" data-media-id="${cell.dataset.mediaId || ''}" aria-current="${i === 0}"><span class="sr-only">${i + 1}</span></button>`).join('')}</div>`);
          dots = gallery.querySelector('.gallery__dots');
        }
        dots.classList.add('gallery__dots--desktop', 'gallery__dots--mobile');
      }
    }
    const title = section.querySelector('.product-title')?.textContent.trim() || '';
    const price = section.querySelector('.product-info__block--price .price-list')?.cloneNode(true);

    let dialog = document.getElementById('quick-buy');
    if (!dialog) {
      document.body.insertAdjacentHTML('beforeend', `
        <x-dialog type="modal" id="quick-buy" class="quick-buy color-${settings.modalScheme || 'scheme-2'}">
          <div class="overlay-backdrop"></div>
          <div class="overlay-panel modal quick-buy__panel" aria-label="${settings.strings.chooseOptions || 'Choose options'}">
            <div class="modal__header quick-buy__header">
              <p class="h5">${settings.strings.chooseOptions || 'Choose options'}</p>
              <button type="button" class="dialog-close" data-dialog-close><span class="sr-only">${settings.strings.close || 'Close'}</span><svg viewBox="0 0 16 16" width="14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m1 1 14 14M15 1 1 15"/></svg></button>
            </div>
            <div class="quick-buy__body"></div>
          </div>
        </x-dialog>`);
      dialog = document.getElementById('quick-buy');
      // Only when quick buy itself closes: a size guide opened from it fires the same (bubbling) event.
      dialog.addEventListener('dialog:after-hide', (event) => {
        if (event.target === dialog) dialog.querySelector('.quick-buy__body').innerHTML = '';
      });
    }
    const body = dialog.querySelector('.quick-buy__body');
    body.innerHTML = '';
    const summary = document.createElement('div');
    summary.className = 'quick-buy__summary';
    summary.innerHTML = `<a href="${page.pathname}" class="h6">${title}</a>`;
    if (price) {
      price.classList.remove('price-list--product');
      price.classList.add('price-list--card');
      summary.append(price);
    }
    const details = document.createElement('a');
    details.href = page.pathname;
    details.className = 'link quick-buy__details';
    details.textContent = settings.strings.viewDetails || 'View details';
    // Page scripts in the section's modals run once the section is added below.
    document.adoptNode(section);
    runModalScripts(section);
    body.append(section);
    section.querySelector('.main-product__gallery')?.after(summary);
    section.querySelector('.main-product__info-inner')?.append(details);
    // Open with the picture in place: the first image loads straight away (a lazy image would wait until the
    // dialog is visible) and the dialog waits for it, at most 1.5s, so text and image appear together.
    // All of its images load straight away: quick buy shows a handful, and a slide still loading when the shopper
    // presses next would have no size to glide to.
    section.querySelectorAll('.gallery__media img').forEach((img) => (img.loading = 'eager'));
    const firstImage = section.querySelector('.gallery__media.is-selected img, .gallery__media img');
    if (firstImage) {
      firstImage.fetchPriority = 'high';
      await Promise.race([firstImage.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 1500))]);
    }
    dialog.show(opener);
  },
};
Theme.QuickBuy = QuickBuy;

document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('[data-quick-buy]');
  if (!trigger) return;
  event.preventDefault();
  trigger.setAttribute('aria-busy', 'true');
  try {
    await QuickBuy.open(trigger.dataset.quickBuy, trigger);
  } finally {
    trigger.removeAttribute('aria-busy');
  }
});

/* ===========================================================================
   Product cards: swatch preview and selection
=========================================================================== */
define('product-card', class extends ThemeElement {
  setup() {
    this.addEventListener('change', (event) => {
      const input = event.target;
      if (!input.closest('[data-card-swatches]')) return;
      this.select(input);
    });
    this.addEventListener('pointerover', (event) => {
      if (event.pointerType !== 'mouse') return;
      const label = event.target.closest('[data-card-swatches] label[for]');
      if (!label) return;
      const input = this.querySelector(`#${CSS.escape(label.htmlFor)}`);
      if (input?.dataset.mediaSrc) this.preview(input.dataset.mediaSrc, input.dataset.mediaSrcset);
    });
    this.addEventListener('pointerout', (event) => {
      if (!event.target.closest('[data-card-swatches]')) return;
      if (this._previewing) this.restore();
    });
    // Image swiping: the bar's thumb follows the scroll position, one image wide, measured from the first real image
    // so the loop's clones at either end leave it at the first or last place.
    this.slides = this.querySelector('.product-card__slides');
    const bar = this.querySelector('.product-card__bar');
    if (this.slides && bar) {
      this.slides.addEventListener('scroll', () => {
        const box = this.slides.getBoundingClientRect();
        const first = this.slides.querySelector(':scope > img:not([data-clone])').getBoundingClientRect();
        const offset = getComputedStyle(this.slides).direction === 'rtl' ? first.right - box.right : box.left - first.left;
        const count = this.slides.cells.length;
        bar.style.setProperty('--at', Math.min(Math.max(offset / box.width, 0), count - 1));
      }, { passive: true });
    }
  }

  get primary() {
    return this.querySelector('.product-card__image--primary');
  }

  preview(src, srcset) {
    const img = this.primary;
    if (!img) return;
    if (!this._original) this._original = { src: img.src, srcset: img.srcset };
    this._previewing = true;
    img.srcset = srcset || '';
    img.src = src;
  }

  restore() {
    const img = this.primary;
    if (!img || !this._original) return;
    img.srcset = this._original.srcset;
    img.src = this._original.src;
    this._previewing = false;
  }

  async select(input) {
    const url = input.dataset.productUrl;
    if (url) this.querySelectorAll('[data-card-link]').forEach((a) => (a.href = url));
    const info = this.querySelector(`template[data-card-info="${input.value}"]`);
    if (info) this.showProduct(info.content, input.value, url);
    const img = this.primary;
    if (!img || !input.dataset.mediaSrc) return;
    this._previewing = false;
    const media = img.closest('.product-card__media');
    media.classList.add('is-swapping');
    const next = new Image();
    next.srcset = input.dataset.mediaSrcset || '';
    next.sizes = img.sizes;
    next.src = input.dataset.mediaSrc;
    await Promise.all([next.decode().catch(() => {}), Theme.wait(150)]);
    img.srcset = next.srcset;
    img.src = next.src;
    this._original = { src: img.src, srcset: img.srcset };
    const secondary = this.querySelector('.product-card__image--secondary');
    if (secondary && input.dataset.secondaryMediaSrc) {
      secondary.srcset = '';
      secondary.src = input.dataset.secondaryMediaSrc;
    }
    if (this.slides) this.showColor(input.dataset.cardImages);
    media.classList.remove('is-swapping');
  }

  /* A linked colour is its own product: the card takes over its title, price, badges and quick add. A card without
     quick add or badges keeps going without; a sold-out colour hides the quick add. */
  showProduct(info, productId, url) {
    this.dataset.productId = productId;
    if (url) this.dataset.url = url;
    const title = this.querySelector('.product-card__title');
    if (title) title.textContent = info.querySelector('[data-card-title]').textContent;
    const price = info.querySelector('.price-list');
    if (price) this.querySelector('.product-card__title-price .price-list')?.replaceWith(price.cloneNode(true));
    if (this.hasAttribute('data-show-badges')) {
      const figure = this.querySelector('.product-card__figure');
      figure.querySelectorAll(':scope > .product-card__badges').forEach((badges) => badges.remove());
      figure.prepend(...[...info.querySelectorAll('.product-card__badges')].map((badges) => badges.cloneNode(true)));
    }
    const quickAdd = this.querySelector('.product-card__figure > .quick-add');
    if (quickAdd) {
      const next = info.querySelector('.quick-add');
      if (next) quickAdd.replaceWith(next.cloneNode(true));
      else quickAdd.hidden = true;
    }
  }

  /* Swiping cards: a chosen colour swipes through its own images (the first one is swapped by select), from the start.
     The card keeps its number of slides; a colour with fewer images hides the rest. */
  showColor(cardImages) {
    const urls = (cardImages || '').split(',').filter(Boolean);
    const slides = [...this.slides.querySelectorAll(':scope > img:not([data-clone])')];
    slides.slice(1).forEach((img, i) => {
      const url = urls[i + 1];
      img.hidden = !url;
      if (!url) return;
      img.srcset = '';
      img.src = url;
    });
    const count = Math.max(1, Math.min(urls.length, slides.length));
    const slider = this.querySelector('.product-card__slider');
    slider.hidden = count < 2;
    slider.style.setProperty('--count', count);
    slider.querySelectorAll('[data-carousel="dot"]').forEach((dot, i) => (dot.hidden = i >= count));
    this.slides.dispatchEvent(new CustomEvent('carousel:filter'));
    this.slides.select(0, 'auto');
  }
});

/* ===========================================================================
   Asynchronous product lists
=========================================================================== */
define('product-recommendations', class extends ThemeElement {
  setup() {
    if (this.children.length || this.loaded) return;
    this.loaded = true;
    whenIdle(async () => {
      const fresh = await fetchSectionElement(this.dataset.url, 'product-recommendations');
      if (!fresh || !fresh.children.length) return this.closest('.shopify-section')?.remove();
      this.innerHTML = fresh.innerHTML;
      this.hidden = false;
      window.Theme.joinSections?.();
    });
  }
});

define('recently-viewed', class extends ThemeElement {
  setup() {
    if (this.children.length || this.loaded) return;
    this.loaded = true;
    let ids = [];
    try {
      ids = JSON.parse(localStorage.getItem('brandlabs:recently-viewed') || '[]');
    } catch (e) {}
    const exclude = Number(this.dataset.exclude);
    ids = ids.filter((id) => id !== exclude).slice(0, Number(this.dataset.limit) || 9);
    if (!ids.length) return this.closest('.shopify-section')?.classList.add('is-empty');
    whenIdle(async () => {
      const url = new URL(settings.routes.search, location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', this.dataset.sectionId);
      const fresh = await fetchSectionElement(url, 'recently-viewed');
      if (!fresh || !fresh.children.length) return;
      // Search returns relevance order; show the products in the order they were viewed.
      const list = fresh.querySelector('reveal-group');
      if (list) {
        const position = (item) => ids.indexOf(Number((item.querySelector('product-card') || item).dataset.productId));
        [...list.children].sort((a, b) => position(a) - position(b)).forEach((item) => list.append(item));
      }
      this.innerHTML = fresh.innerHTML;
      this.hidden = false;
      window.Theme.joinSections?.();
    });
  }
});

/* ===========================================================================
   Collection and search: filters, sort, pagination, grid modes
=========================================================================== */
class CollectionSection extends ThemeElement {
  // Sheets and drawers (filters, sort) move to <body> while open, so events are taken from the document.
  owns(target) {
    return belongsTo(this, target);
  }

  setup() {
    document.addEventListener('change', (event) => {
      if (!this.owns(event.target)) return;
      const form = event.target.closest('[data-facets-form]');
      if (!form || event.target.closest('price-range [type="range"]')) return;
      const inDrawer = Boolean(form.closest('[data-facet-drawer]'));
      this.apply(this.urlFromForm(form), { prefetchOnly: inDrawer && this.drawerOpen, focusId: event.target.id });
    });
    document.addEventListener('click', (event) => {
      if (!this.owns(event.target)) return;
      const link = event.target.closest('[data-facet-url]');
      if (link) {
        event.preventDefault();
        return this.apply(new URL(link.dataset.facetUrl, location.origin));
      }
      const sort = event.target.closest('[data-sort]');
      if (sort) {
        sort.closest('x-dialog')?.hide();
        const url = new URL(location.href);
        url.searchParams.set('sort_by', sort.dataset.sort);
        url.searchParams.delete('page');
        return this.apply(url);
      }
      const mode = event.target.closest('[data-mode]');
      if (mode) this.setMode(mode.dataset.device, mode.dataset.mode);
    });
    document.addEventListener('dialog:show', (e) => {
      if (e.target.matches?.('[data-facet-drawer]')) this.drawerOpen = true;
    });
    document.addEventListener('dialog:hide', (e) => {
      if (!e.target.matches?.('[data-facet-drawer]')) return;
      this.drawerOpen = false;
      if (this.pendingUrl) {
        const url = this.pendingUrl;
        this.pendingUrl = null;
        setTimeout(() => this.apply(url), 320); // after the drawer's 300ms close, so the grid swap is not hidden behind it
      }
    });
    this.toolbar = this.querySelector('[data-toolbar]');
    if (this.toolbar) {
      new ResizeObserver(() => this.style.setProperty('--toolbar-height', `${this.toolbar.offsetHeight}px`)).observe(this.toolbar);
    }
  }

  urlFromForm(form) {
    const url = new URL(location.pathname, location.origin);
    const data = new FormData(form);
    for (const [key, value] of data.entries()) {
      if (value === '' && (key.startsWith('filter.v.price') || key === 'sort_by')) continue;
      url.searchParams.append(key, value);
    }
    const sort = new URL(location.href).searchParams.get('sort_by');
    if (sort && !url.searchParams.has('sort_by')) url.searchParams.set('sort_by', sort);
    url.searchParams.delete('page');
    return url;
  }

  async apply(url, { prefetchOnly = false, focusId } = {}) {
    url = new URL(url, location.origin);
    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    Theme.loading.start();
    try {
      const html = await (await fetch(fetchUrl, { signal })).text();
      if (prefetchOnly) {
        this.pendingUrl = url;
        this.renderDrawerOnly(html);
        return;
      }
      history.replaceState({}, '', url);
      this.render(html, focusId);
    } catch (e) {
      if (e.name !== 'AbortError') console.error(e);
    } finally {
      Theme.loading.end();
    }
  }

  openStates() {
    const states = {};
    document.querySelectorAll('[data-facets-form] [data-filter]').forEach((g) => (states[g.closest('[data-facets-form]').id + g.dataset.filter] = g.querySelector('details').open));
    return states;
  }

  restoreOpenStates(root, states) {
    root.querySelectorAll('[data-facets-form] [data-filter]').forEach((g) => {
      const key = g.closest('[data-facets-form]').id + g.dataset.filter;
      if (key in states) g.querySelector('details').open = states[key];
    });
  }

  renderDrawerOnly(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const freshForm = doc.querySelector('#drawer-form');
    const form = document.querySelector('#drawer-form');
    if (!freshForm || !form) return;
    const states = this.openStates();
    form.innerHTML = freshForm.innerHTML;
    this.restoreOpenStates(form.parentElement, states);
  }

  render(html, focusId) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(this.localName);
    if (!fresh) return;
    const states = this.openStates();
    // A drawer re-parented to <body> would be orphaned by the swap below.
    document.querySelectorAll('body > [data-facet-drawer]').forEach((d) => d.remove());
    const drawerScroll = document.querySelector('[data-facet-drawer] .drawer__body')?.scrollTop;
    this.innerHTML = fresh.innerHTML;
    this.restoreOpenStates(this, states);
    if (drawerScroll) this.querySelector('[data-facet-drawer] .drawer__body').scrollTop = drawerScroll;
    if (focusId) this.querySelector(`#${CSS.escape(focusId)}`)?.focus({ preventScroll: true });
    const area = this.querySelector('.collection-area, .search-results__products');
    if (area) {
      const offset = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-offset')) || 0) + (this.toolbar?.offsetHeight || 0) + 20;
      const top = area.getBoundingClientRect().top + scrollY - offset;
      if (scrollY > top) window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
    this.toolbar = this.querySelector('[data-toolbar]');
  }

  setMode(device, mode) {
    const grid = this.querySelector('[data-grid]');
    if (!grid) return;
    grid.classList.forEach((c) => c.startsWith(`collection-grid--${device}-`) && grid.classList.remove(c));
    grid.classList.add(`collection-grid--${device}-${mode}`);
    this.querySelectorAll(`[data-mode][data-device="${device}"]`).forEach((b) => b.setAttribute('aria-checked', String(b.dataset.mode === mode)));
    grid.replay?.();
    fetch(`${settings.routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { [`_collection_${device}_grid`]: mode } }),
    });
  }
}
define('collection-section', CollectionSection);

/* Price range: slider and fields stay in sync */
define('price-range', class extends ThemeElement {
  setup() {
    const [rMin, rMax] = [this.querySelector('[data-range-min]'), this.querySelector('[data-range-max]')];
    const [iMin, iMax] = [this.querySelector('[data-input-min]'), this.querySelector('[data-input-max]')];
    const max = Number(this.dataset.max);
    const paint = () => {
      this.style.setProperty('--range-min', `${(Number(rMin.value) / max) * 100}%`);
      this.style.setProperty('--range-max', `${(Number(rMax.value) / max) * 100}%`);
    };
    const fromRange = () => {
      if (Number(rMin.value) >= Number(rMax.value)) rMin.value = Number(rMax.value) - 1;
      iMin.value = Number(rMin.value) > 0 ? rMin.value : '';
      iMax.value = Number(rMax.value) < max ? rMax.value : '';
      paint();
    };
    [rMin, rMax].forEach((r) => {
      r.addEventListener('input', fromRange);
      r.addEventListener('change', () => iMax.dispatchEvent(new Event('change', { bubbles: true })));
    });
    [iMin, iMax].forEach((i) =>
      i.addEventListener('change', () => {
        rMin.value = iMin.value || 0;
        rMax.value = iMax.value || max;
        if (Number(rMin.value) >= Number(rMax.value)) {
          rMin.value = Math.max(0, Number(rMax.value) - 1);
          if (iMin.value) iMin.value = rMin.value;
        }
        paint();
      })
    );
    paint();
  }
});

/* Search page tabs reuse x-tabs; search filters reuse collection-section */
define('search-section', class extends CollectionSection {});

/* Quick order list */
define('quick-order-list', class extends ThemeElement {
  setup() {
    this.timers = {};
    this.addEventListener('change', (event) => {
      const selector = event.target.closest('[data-quick-order-quantity]');
      if (!selector) return;
      const id = selector.dataset.quickOrderQuantity;
      clearTimeout(this.timers[id]);
      this.timers[id] = setTimeout(() => this.update({ [id]: Number(event.target.value) }, selector), 300);
    });
    this.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-quick-toggle]');
      if (toggle) this.toggleGroup(toggle.dataset.quickToggle, toggle.getAttribute('aria-expanded') !== 'true');
      const remove = event.target.closest('[data-quick-remove]');
      if (remove) this.update({ [remove.dataset.quickRemove]: 0 });
      if (event.target.closest('[data-remove-all]')) {
        const updates = {};
        this.querySelectorAll('[data-quick-order-quantity]').forEach((q) => (updates[q.dataset.quickOrderQuantity] = 0));
        this.update(updates);
      }
    });
  }

  toggleGroup(id, open) {
    this.querySelector(`[data-quick-toggle="${id}"]`)?.setAttribute('aria-expanded', String(open));
    this.querySelectorAll(`[data-quick-group="${id}"]`).forEach((row) => (row.hidden = !open));
  }

  async update(updates, selector) {
    selector?.setAttribute('aria-busy', 'true');
    try {
      const response = await Theme.fetch(`${settings.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw data;
      await Theme.wait(250);
      const doc = await Theme.fetchSection(location.href, this.dataset.sectionId);
      const fresh = doc.querySelector('quick-order-list');
      if (fresh) {
        const open = [...this.querySelectorAll('[data-quick-toggle][aria-expanded="true"]')].map((t) => t.dataset.quickToggle);
        this.innerHTML = fresh.innerHTML;
        open.forEach((id) => this.toggleGroup(id, true));
      }
      Theme.Cart.refresh();
      document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: data } }));
    } catch (error) {
      const input = selector?.querySelector('input');
      if (input) input.value = input.dataset.previous;
      const err = selector?.closest('td')?.querySelector('.line-item__error');
      if (err) {
        err.textContent = error.description || error.message || settings.strings.cartError;
        err.hidden = false;
      }
    } finally {
      selector?.removeAttribute('aria-busy');
    }
  }
});

/* ===========================================================================
   Slideshow: leave-then-enter fade carousel with autoplay ring
=========================================================================== */
const EASE_IN_QUAD = 'cubic-bezier(0.55, 0.055, 0.675, 0.19)';
const EASE_OUT_QUAD = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
const EASE_OUT_CUBIC = 'cubic-bezier(0.215, 0.61, 0.355, 1)';

class XSlideshow extends ThemeElement {
  setup() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.index = 0;
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.volume = this.querySelector('[data-volume]');
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.dot))));
    this.querySelector('[data-next-section]')?.addEventListener('click', () => {
      const section = this.closest('.shopify-section');
      const next = section?.nextElementSibling;
      const top = (next || section).getBoundingClientRect().top + scrollY - (next ? 0 : -section.offsetHeight);
      window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
    });
    this.volume?.addEventListener('click', () => this.toggleSound());
    if (this.slides.length > 1) {
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.go(this.index - 1);
        if (e.key === 'ArrowRight') this.go(this.index + 1);
      });
      this.setupSwipe();
    }
    document.addEventListener('visibilitychange', () => (document.hidden ? this.pauseTimer() : this.resumeTimer()));
    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', (e) => {
        this.locked = true;
        this.stopTimer();
        this.go(this.slides.indexOf(e.target.closest('[data-slide]')), e.detail?.load);
      });
      this.addEventListener('shopify:block:deselect', () => {
        this.locked = false;
        this.startTimer();
      });
    }
    this.updateVolume();
    new IntersectionObserver((entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      this.appear();
    }).observe(this);
  }

  visibleMedia(slide) {
    return [...slide.querySelectorAll('img, video')].filter((el) => el.offsetParent !== null || el.getClientRects().length);
  }

  async waitForMedia(slide) {
    await Promise.all(
      this.visibleMedia(slide).map((el) =>
        el.tagName === 'IMG'
          ? el.complete ? Promise.resolve() : new Promise((r) => el.addEventListener('load', r, { once: true }) || el.addEventListener('error', r, { once: true }))
          : el.readyState >= 3 ? Promise.resolve() : new Promise((r) => el.addEventListener('canplay', r, { once: true }))
      )
    );
  }

  async appear() {
    const slide = this.slides[0];
    this.querySelectorAll('video').forEach((v) => v.play().catch(() => {}));
    await this.waitForMedia(slide);
    if (this.hasAttribute('initial-transition') && !reducedMotion() && !window.Shopify?.designMode) {
      this.classList.add('is-ready');
      await this.enter(slide);
    }
    this.classList.add('is-ready');
    this.startTimer();
  }

  parts(slide) {
    return {
      media: slide.querySelector('[data-slide-media]'),
      content: slide.querySelector('[data-slide-content]'),
      text: slide.querySelector('[data-slide-text]'),
      buttons: slide.querySelector('[data-slide-buttons]'),
      boxed: slide.classList.contains('slideshow__slide--boxed'),
    };
  }

  leave(slide) {
    const p = this.parts(slide);
    const anims = [];
    const opts = (duration, delay = 0) => ({ duration: duration * 1000, delay: delay * 1000, easing: EASE_IN_QUAD, fill: 'forwards' });
    const motion = !reducedMotion();
    if (p.boxed) {
      p.content && anims.push(p.content.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: motion ? 'translateY(20px)' : 'none' }], opts(0.25)));
    } else {
      p.text && anims.push(p.text.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: motion ? 'translateY(10px)' : 'none' }], opts(0.25)));
      p.buttons && anims.push(p.buttons.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: motion ? 'translateY(20px)' : 'none' }], opts(0.25)));
    }
    p.media && anims.push(p.media.animate([{ opacity: 1 }, { opacity: 0 }], opts(0.2, 0.15)));
    return Promise.all(anims.map((a) => a.finished.catch(() => {})));
  }

  enter(slide) {
    const p = this.parts(slide);
    const motion = !reducedMotion();
    const anims = [
      slide.animate({ opacity: [0, 1] }, { duration: 800, easing: EASE_OUT_QUAD }),
      p.media?.animate([{ opacity: 0, transform: motion ? 'scale(1.2)' : 'none' }, { opacity: 1, transform: 'none' }], { duration: 800, easing: EASE_OUT_QUAD }),
    ];
    const target = p.boxed ? p.content : p.text;
    target && anims.push(target.animate([{ opacity: 0, transform: motion ? 'translateY(30px)' : 'none' }, { opacity: 1, transform: 'none' }], { duration: 600, delay: 400, easing: EASE_OUT_CUBIC, fill: 'backwards' }));
    if (!p.boxed && p.buttons) anims.push(p.buttons.animate([{ opacity: 0, transform: motion ? 'translateY(20px)' : 'none' }, { opacity: 1, transform: 'none' }], { duration: 600, delay: 600, easing: EASE_OUT_CUBIC, fill: 'backwards' }));
    return Promise.all(anims.filter(Boolean).map((a) => a.finished.catch(() => {})));
  }

  async go(target, instant = false) {
    const count = this.slides.length;
    if (count < 2) return;
    const next = ((target % count) + count) % count;
    if (next === this.index) return;
    const from = this.slides[this.index];
    const to = this.slides[next];
    this.index = next;
    // A tap during a transition cuts it short, so exactly one slide is ever selected.
    const token = (this.token = (this.token || 0) + 1);
    this.slides.forEach((slide) => {
      slide.getAnimations({ subtree: true }).forEach((a) => a.cancel());
      slide.classList.toggle('is-selected', slide === from);
    });
    this.stopTimer();
    this.dots.forEach((d, i) => d.setAttribute('aria-current', String(i === next)));
    from.querySelectorAll('video').forEach((v) => {
      v.muted = true;
      v.pause();
    });
    [next - 1, next + 1].forEach((i) => this.slides[(i + count) % count].querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager')));
    if (!instant) await this.leave(from);
    if (token !== this.token) return;
    from.getAnimations({ subtree: true }).forEach((a) => a.cancel());
    from.classList.remove('is-selected');
    from.setAttribute('aria-hidden', 'true');
    to.classList.add('is-selected');
    to.removeAttribute('aria-hidden');
    to.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    this.visibleMedia(to).filter((el) => el.tagName === 'VIDEO').forEach((v) => {
      v.muted = true;
      v.currentTime = 0;
      v.play().catch(() => {});
    });
    this.updateVolume();
    if (!this.locked) this.startTimer();
    if (!instant) await this.enter(to);
  }

  get autoplay() {
    return Number(this.getAttribute('autoplay')) || 0;
  }

  startTimer() {
    if (!this.autoplay || this.slides.length < 2 || this.locked) return;
    clearTimeout(this.timer);
    const slide = this.slides[this.index];
    const video = this.visibleMedia(slide).find((el) => el.tagName === 'VIDEO');
    const dot = this.dots[this.index];
    this.dots.forEach((d) => {
      d.classList.remove('is-running');
      d.style.removeProperty('--ring-duration');
    });
    void dot?.offsetWidth;
    if (video && this.hasAttribute('pause-on-video')) {
      const begin = () => {
        dot?.style.setProperty('--ring-duration', `${video.duration}s`);
        dot?.classList.add('is-running');
      };
      video.readyState >= 1 ? begin() : video.addEventListener('loadedmetadata', begin, { once: true });
      video.onended = () => this.go(this.index + 1);
      return;
    }
    dot?.classList.add('is-running');
    this.remaining = this.autoplay * 1000;
    this.startedAt = Date.now();
    this.timer = setTimeout(() => this.go(this.index + 1), this.remaining);
  }

  stopTimer() {
    clearTimeout(this.timer);
    this.dots.forEach((d) => d.classList.remove('is-running'));
    this.slides.forEach((s) => s.querySelectorAll('video').forEach((v) => (v.onended = null)));
  }

  pauseTimer() {
    if (!this.timer || !this.startedAt) return;
    clearTimeout(this.timer);
    this.remaining -= Date.now() - this.startedAt;
    this.classList.add('is-paused');
    this.paused = true;
  }

  resumeTimer() {
    if (!this.paused) return;
    this.paused = false;
    this.classList.remove('is-paused');
    this.startedAt = Date.now();
    this.timer = setTimeout(() => this.go(this.index + 1), Math.max(0, this.remaining));
  }

  currentVideo() {
    return this.visibleMedia(this.slides[this.index]).find((el) => el.tagName === 'VIDEO');
  }

  updateVolume() {
    if (!this.volume) return;
    const slide = this.slides[this.index];
    const video = this.currentVideo();
    const allowed = slide.hasAttribute('data-allow-sound');
    this.volume.hidden = !video || !allowed;
    const muted = !video || video.muted;
    this.volume.querySelector('[data-muted-icon]').hidden = !muted;
    this.volume.querySelector('[data-unmuted-icon]').hidden = muted;
    this.volume.setAttribute('aria-label', muted ? this.volume.dataset.unmuteLabel : this.volume.dataset.muteLabel);
  }

  toggleSound() {
    const video = this.currentVideo();
    if (!video) return;
    video.muted = !video.muted;
    this.updateVolume();
  }

  setupSwipe() {
    onSwipe(this, (direction) => this.go(this.index + direction), { holdPage: true });
  }
}
define('x-slideshow', XSlideshow);

/* Image with text: image slides in from the text side */
define('image-reveal', class extends ThemeElement {
  setup() {
    const side = this.dataset.revealImage;
    if (!side) return;
    if (reducedMotion()) return this.classList.add('is-revealed');
    new IntersectionObserver(async (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      const img = [...this.querySelectorAll('img')].find((i) => i.offsetParent !== null) || this.querySelector('img');
      if (img && !img.complete) await new Promise((r) => img.addEventListener('load', r, { once: true }) || img.addEventListener('error', r, { once: true }));
      const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      const distance = (isDesktop() ? 25 : 15) * (side === 'end' ? 1 : -1) * rtl;
      this.classList.add('is-revealed');
      this.animate([{ opacity: 0, transform: `translateX(${distance}px)` }, { opacity: 1, transform: 'none' }], { duration: 300, easing: EASE_OUT_CUBIC });
    }, { rootMargin: '-200px 0px 0px 0px' }).observe(this);
  }
});

/* Image with text overlay: banner, image and content fade in sequence */
define('overlay-reveal', class extends ThemeElement {
  setup() {
    if (!this.hasAttribute('data-reveal-overlay')) return;
    if (reducedMotion() || window.Shopify?.designMode) return this.classList.add('is-revealed');
    new IntersectionObserver(async (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      const img = this.querySelector('[data-overlay-media] img');
      if (img && !img.complete) await new Promise((r) => img.addEventListener('load', r, { once: true }) || img.addEventListener('error', r, { once: true }));
      this.classList.add('is-revealed');
      const media = this.querySelector('[data-overlay-media]');
      const content = this.querySelector('[data-overlay-content]');
      this.animate({ opacity: [0, 1] }, { duration: 300, easing: 'ease' });
      media?.animate([{ opacity: 0, transform: 'scale(1.1)' }, { opacity: 1, transform: 'none' }], { duration: 300, delay: 300, easing: 'ease', fill: 'backwards' });
      content?.animate({ opacity: [0, 1] }, { duration: 300, delay: 600, easing: 'ease', fill: 'backwards' });
    }, { threshold: 0.05 }).observe(this);
  }
});

/* Parallax: image moves ±11.5% of its height while its section crosses the viewport */
Theme.parallax = (container, target) => {
  if (reducedMotion()) return;
  const update = () => {
    const rect = container.getBoundingClientRect();
    const first = container.closest('.shopify-section') === document.querySelector('#main > .shopify-section');
    const start = first ? 0 : innerHeight;
    const travel = first ? rect.height : innerHeight + rect.height;
    const progress = Math.min(1, Math.max(0, (start - rect.top) / travel));
    target.style.translate = `0 ${(-11.5 + 23 * progress).toFixed(2)}%`;
  };
  update();
  addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
  addEventListener('resize', update);
};

define('collection-banner', class extends ThemeElement {
  setup() {
    const images = this.querySelectorAll('.collection-banner__image');
    const parallax = this.classList.contains('collection-banner--parallax');
    if (parallax) images.forEach((img) => Theme.parallax(this, img));
    if (!this.classList.contains('collection-banner--reveal')) return;
    if (reducedMotion()) return this.classList.add('is-revealed');
    new IntersectionObserver(async (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      const img = [...images].find((i) => i.offsetParent !== null) || images[0];
      if (img && !img.complete) await new Promise((r) => img.addEventListener('load', r, { once: true }) || img.addEventListener('error', r, { once: true }));
      this.classList.add('is-revealed');
      const from = parallax ? 1.5 : 1.2;
      const to = parallax ? 1.3 : 1;
      images.forEach((i) => i.animate([{ opacity: 0, scale: from }, { opacity: 1, scale: to }], { duration: 800, delay: 250, easing: EASE_OUT_QUAD, fill: 'backwards' }));
      this.querySelector('.collection-banner__content')?.animate([{ opacity: 0, transform: 'translateY(30px)' }, { opacity: 1, transform: 'none' }], { duration: 600, delay: 650, easing: EASE_OUT_CUBIC, fill: 'backwards' });
    }).observe(this);
  }
});
